import React, { useEffect, useRef, useState } from "react";
import "../css/Appointment.css";
import "../css/AppointmentLegend.css";
import CustomSelect from "../components/CustomSelect";
import CustomButton from "../components/CustomButton";
import CustomTextField from "../components/CustomTextField";
import GridCalendar from "../components/GridCalendar";
import { Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton, Divider } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import InvoiceItemsEditor, { InvoiceItem } from "../components/invoices/InvoiceItemsEditor";
import {
  createAppointment,
  updateAppointment,
  deleteAppointment,
  getAllAppointments,
  changeAppointmentStatus,
} from "../services/AppointmentService";
import { getAllBranches } from "../services/BranchesService";
import { getOrgClients, getClientById, updateClient } from "../services/ClientsService";
import { manageSlots, getSlots } from "../services/SlotService";
import { getAllServices, getBranchServices } from "../services/ServicesService";
import { createInvoice, getInvoiceByAppointmentId, updateInvoice } from "../services/InvoicesService";
import { hasFifteenMinuteSlots } from "../services/VenueService";
import { getUserId } from "../services/authService";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import AddClientDialog from "../components/AddClientDialog";
import { toast } from "react-toastify";
import CustomLabel from "../components/CustomLabel";
import { COLORS } from "../assets/theme";
import { countries } from "../assets/utils/countries";

const buildTimeOptions = (stepMinutes: number) => {
  const safeStep = Math.max(1, Math.min(60, stepMinutes || 30));
  const totalSteps = Math.floor((24 * 60) / safeStep);
  return Array.from({ length: totalSteps }, (_, i) => {
    const totalMinutes = i * safeStep;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(
      2,
      "0"
    )}`;
    return { label: value, value };
  });
};

const normalizeClientCountryCode = (value?: unknown): string => {
  if (value === undefined || value === null) return "";
  const stringValue = typeof value === "string" ? value : String(value);
  const match = stringValue.match(/^\s*(\+?\d+)/);
  const dialCode = match ? match[1] : stringValue.trim();
  if (!dialCode) return "";
  return dialCode.startsWith("+") ? dialCode : `+${dialCode}`;
};

const normalizeClientGenderValue = (value?: unknown): string => {
  if (value === undefined || value === null) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lower = str.toLowerCase();
  if (lower === "m" || lower === "male") return "male";
  if (lower === "f" || lower === "female") return "female";
  return str;
};

const formatClientDateOnly = (value?: unknown): string => {
  if (value === undefined || value === null) return "";
  const str = typeof value === "string" ? value.trim() : String(value);
  if (!str) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (str.includes("T")) return str.split("T")[0];
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().split("T")[0];
};

interface AppointmentEvent {
  id: string | number;
  text: string;
  start_date: Date;
  end_date: Date;
  customer_name?: string;
  phone?: string;
  court?: string;
  status?: "confirmed" | "pending" | "cancelled";
  duration?: number;
}

interface Court {
  id: string;
  name: string;
  type: string;
  color: string;
}

interface Client {
  id: string;
  _id?: string;
  name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  country_code?: string;
  gender?: string;
  dob?: string;
}

interface Service {
  id: string;
  _id?: string;
  name?: string;
  title?: string;
  service_name?: string;
  label?: string;
  service_id?: string;
  duration?: number;
  price?: number;
}

interface ClientsResponse {
  users: Client[];
}

const normalizeClientRecord = (raw: any): Client => {
  if (!raw) return { id: "" };
  const id =
    raw.id ||
    raw._id ||
    raw.client_id ||
    raw.user_id ||
    raw.value ||
    raw.userId ||
    raw.clientId ||
    "";
  const name =
    raw.name ||
    raw.fullName ||
    raw.full_name ||
    raw.label ||
    raw.email ||
    (id ? `Client ${id}` : "Client");
  const countryCode = normalizeClientCountryCode(
    raw.country_code || raw.countryCode || ""
  );
  const phoneCandidate =
    raw.mobile ||
    raw.phone ||
    raw.phoneNumber ||
    raw.contact ||
    raw.number ||
    raw.mobile_number ||
    raw.telephone ||
    "";
  const labelPhoneMatch =
    typeof raw.label === "string"
      ? raw.label.match(/(\+?\d[\d\s-]*)$/)?.[1] || ""
      : "";
  const basePhone = String(phoneCandidate || labelPhoneMatch || "").trim();
  const prefixedPhone =
    basePhone || !countryCode
      ? basePhone
      : `${countryCode}${basePhone.replace(/^\+?/, "")}`;
  const gender = normalizeClientGenderValue(
    raw.gender ||
      raw.sex ||
      raw.gender_identity ||
      raw.genderIdentity ||
      ""
  );
  const dob = formatClientDateOnly(
    raw.dob ||
      raw.date_of_birth ||
      raw.dateOfBirth ||
      raw.birth_date ||
      raw.birthDate ||
      ""
  );
  return {
    id: String(id),
    name: String(name).trim(),
    email: raw.email || raw.mail || "",
    phone: prefixedPhone || basePhone,
    mobile: prefixedPhone || basePhone,
    country_code: countryCode,
    gender,
    dob,
  };
};

const formatClientLabel = (client?: Client) => {
  if (!client) return "Client";
  const name =
    client.name ||
    client.email ||
    (client.id ? `Client ${client.id}` : "Client");
  const phone = client.mobile || client.phone || "";
  const digitsOnly = (val: string) => val.replace(/\D+/g, "");
  const phoneDigits = digitsOnly(phone);
  const nameDigits = digitsOnly(name);
  if (phone && phoneDigits && nameDigits.includes(phoneDigits)) {
    return name;
  }
  return phone ? `${name} - ${phone}` : name;
};

function Appointment() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const venueId = location.state?.venueId || params.branchId || null;

  const [selectedCourt, setSelectedCourt] = useState<string>("all");
  const [allAppointments, setAllAppointments] = useState<AppointmentEvent[]>(
    []
  );
  const selectedCourtRef = useRef<string>(selectedCourt);
  const [clients, setClients] = useState<ClientsResponse | null>(null);
  const branchesRef = useRef<any[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const servicesRef = useRef<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  const [branches, setBranches] = useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("all");
  const [embedUrl, setEmbedUrl] = useState<string>(
    "https://calendar.google.com/calendar/embed?src=en.indian%23holiday%40group.v.calendar.google.com&ctz=UTC"
  );
  const [events, setEvents] = useState<any[]>([]);
  const [activeDate, setActiveDate] = useState<Date>(new Date());
  const branchCalendarRefs = useRef<Record<string, any>>({});
  const schedulerWrapperRef = useRef<HTMLDivElement | null>(null);

  const displayedBranches = React.useMemo(() => {
    const list = Array.isArray(branches) ? branches : [];
    if (selectedBranchId === "all") return list;
    return list.filter(
      (item: any) => String(item.id) === String(selectedBranchId)
    );
  }, [branches, selectedBranchId]);

  const displayedBranchIds = React.useMemo(
    () =>
      displayedBranches.map((branch: any) => String(branch.id)).filter(Boolean),
    [displayedBranches]
  );
  const isSingleBranch = displayedBranchIds.length <= 1;
  const axisBranchId = !isSingleBranch ? displayedBranchIds[0] || "" : "";

  const eventsByBranch = React.useMemo(() => {
    const grouped: Record<string, any[]> = {};
    (Array.isArray(events) ? events : []).forEach((ev: any) => {
      const branchId = ev?.extendedProps?.branchId;
      if (!branchId) return;
      const key = String(branchId);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(ev);
    });
    return grouped;
  }, [events]);

  const [currentView, setCurrentView] = useState<"day" | "week" | "month">(
    "day"
  );
  const viewType =
    currentView === "month"
      ? "dayGridMonth"
      : currentView === "week"
      ? "timeGridWeek"
      : "timeGridDay";
  const isMobileInteraction = React.useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const userAgent = navigator.userAgent || "";
    const isMobileAgent =
      /Android|iPhone|iPad|iPod|Windows Phone/i.test(userAgent);
    const isIpadOs =
      /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 0;
    const uaDataMobile = Boolean(
      (navigator as any).userAgentData?.mobile
    );
    const hasTouch = navigator.maxTouchPoints > 0;
    const coarsePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    const smallViewport =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 900px)").matches;
    return (
      isMobileAgent ||
      uaDataMobile ||
      isIpadOs ||
      coarsePointer ||
      (hasTouch && smallViewport)
    );
  }, []);
  const displayedBranchKey = React.useMemo(
    () => displayedBranchIds.join("|"),
    [displayedBranchIds]
  );
  const shiftActiveDate = (direction: -1 | 1) => {
    setActiveDate((prev) => {
      if (currentView === "month") {
        const year = prev.getFullYear();
        const month = prev.getMonth() + direction;
        const day = prev.getDate();
        const target = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        target.setDate(Math.min(day, daysInMonth));
        return target;
      }
      const next = new Date(prev);
      if (currentView === "week") {
        next.setDate(next.getDate() + 7 * direction);
      } else {
        next.setDate(next.getDate() + direction);
      }
      return next;
    });
  };
  const gotoPrev = () => shiftActiveDate(-1);
  const gotoNext = () => shiftActiveDate(1);
  const gotoToday = () => setActiveDate(new Date());
  const gotoDate = (d: Date) => setActiveDate(d);
  const handleViewChange = (nextView: "day" | "week" | "month") => {
    if (nextView !== "day" && selectedBranchId === "all" && branches.length > 0) {
      setSelectedBranchId(String(branches[0].id));
    }
    setCurrentView(nextView);
  };
  const handleBranchChange = (value: string) => {
    const nextValue = String(value);
    if (nextValue === "all" && currentView !== "day") {
      setCurrentView("day");
    }
    setSelectedBranchId(nextValue);
  };
  const [visibleStart, setVisibleStart] = useState<Date>(new Date());
  const [visibleEnd, setVisibleEnd] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d;
  });
  const [calendarKey, setCalendarKey] = useState<number>(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const clientFieldHelpersRef = useRef<{
    setFieldValue?: (name: string, value: any) => void;
    values?: Record<string, any>;
  } | null>(null);
  const [formDate, setFormDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [formTimeStart, setFormTimeStart] = useState<string>("09:00");
  const [formTimeEnd, setFormTimeEnd] = useState<string>("10:00");
  const [formCustomerName, setFormCustomerName] = useState<string>("");
  const [formNotes, setFormNotes] = useState<string>("");
  const [formClientId, setFormClientId] = useState<string>("");
  const [formServiceId, setFormServiceId] = useState<string>("");
  const [formStatus, setFormStatus] = useState<string>("Pending");
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editingEventRaw, setEditingEventRaw] = useState<any | null>(null);
  const [editingPastAppointment, setEditingPastAppointment] = useState(false);
  const [invoiceEditOpen, setInvoiceEditOpen] = useState(false);
  const [invoiceEditingRow, setInvoiceEditingRow] = useState<any | null>(null);
  const [invoiceServices, setInvoiceServices] = useState<Service[]>([]);
  const [invoiceEditLoading, setInvoiceEditLoading] = useState(false);
  const [formBranchId, setFormBranchId] = useState<string>("all");
  const [clientEditOpen, setClientEditOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [clientEditLoading, setClientEditLoading] = useState(false);

  const [appointmentDialogTab, setAppointmentDialogTab] = useState(0);
  const [blockAction, setBlockAction] = useState<"open" | "close">("close");
  const [blockDate, setBlockDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [blockStart, setBlockStart] = useState<string>("09:00");
  const [blockEnd, setBlockEnd] = useState<string>("10:00");
  const [slotDuration, setSlotDuration] = useState<string>("00:30:00");
  const [slotDurationLoading, setSlotDurationLoading] =
    useState<boolean>(true);
  const formatTo12h = React.useCallback((value?: string | null) => {
    if (!value) return "-";
    const [hStr = "0", mStr = "0"] = String(value).split(":");
    let h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10) || 0;
    if (isNaN(h)) h = 0;
    const period = h >= 12 ? "pm" : "am";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
  }, []);
  const pickNonEmptyString = (...values: unknown[]): string | undefined => {
    for (const value of values) {
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed) return trimmed;
      }
      if (typeof value === "number") {
        const text = String(value);
        if (text) return text;
      }
    }
    return undefined;
  };
  const resolveClientPhone = (raw: any): string | undefined => {
    return pickNonEmptyString(
      raw?.mobile,
      raw?.phone,
      raw?.phoneNumber,
      raw?.client_phone,
      raw?.customer_phone,
      raw?.patient?.phone,
      raw?.patient?.mobile,
      raw?.client?.phone,
      raw?.client?.mobile,
      raw?.customer?.phone,
      raw?.customer?.mobile,
      raw?.user?.phone,
      raw?.user?.mobile
    );
  };
  const resolveInvoiceStatusFormValue = (
    status: unknown,
    fallbackId?: unknown
  ): "paid" | "unpaid" | "cancelled" => {
    const numeric = Number(status ?? fallbackId);
    if (Number.isFinite(numeric)) {
      if (numeric === 1) return "paid";
      if (numeric === 2) return "unpaid";
      if (numeric === 3) return "cancelled";
    }
    if (typeof status === "string" && status.trim()) {
      const normalized = status.trim().toLowerCase();
      if (
        normalized === "paid" ||
        normalized === "unpaid" ||
        normalized === "cancelled"
      ) {
        return normalized;
      }
    }
    if (typeof fallbackId === "string" && fallbackId.trim()) {
      const normalized = fallbackId.trim().toLowerCase();
      if (
        normalized === "paid" ||
        normalized === "unpaid" ||
        normalized === "cancelled"
      ) {
        return normalized as "paid" | "unpaid" | "cancelled";
      }
    }
    return "unpaid";
  };
  const resolveInvoicePaymentMethodValue = (value: unknown): string => {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      if (numeric === 1) return "card";
      if (numeric === 2) return "cash";
      if (numeric === 3) return "bank transfer";
    }
    if (typeof value === "string" && value.trim()) {
      const normalized = value.trim().toLowerCase();
      if (
        normalized === "cash" ||
        normalized === "card" ||
        normalized === "bank transfer"
      ) {
        return normalized;
      }
    }
    return "cash";
  };
  const formatInvoiceDateValue = (value: any): string => {
    if (!value) return "";
    const dateObj = value instanceof Date ? value : new Date(value);
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  };
  const computeInvoiceItemTotal = (
    quantity: number,
    unitCost: number,
    discount: number
  ) => {
    const qty = Number(quantity) || 0;
    const unit = Number(unitCost) || 0;
    const disc = Math.min(100, Math.max(0, Number(discount) || 0));
    const subtotal = qty * unit;
    const discountAmount = (subtotal * disc) / 100;
    return Math.round((subtotal - discountAmount) * 100) / 100;
  };
  const resolveInvoiceDetails = (row: any): any[] => {
    const candidates = [
      row?.details,
      row?.invoice?.details,
      row?.invoice?.data?.details,
      row?.invoice_details,
      row?.data?.details,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }
    return [];
  };
  const buildInvoiceItems = (
    details: any[],
    servicesList: Service[]
  ): InvoiceItem[] => {
    return (Array.isArray(details) ? details : []).map((detail, index) => {
      const detailServiceId =
        detail?.service_id ?? detail?.serviceId ?? detail?.serviceID;
      const normalizedServiceId =
        detailServiceId !== undefined && detailServiceId !== null
          ? String(detailServiceId)
          : undefined;
      let matchedService = (Array.isArray(servicesList) ? servicesList : []).find(
        (srv: any) =>
          String(srv.id || srv._id || srv.service_id || "") ===
          String(normalizedServiceId || "")
      );

      if (!matchedService && detail?.title) {
        matchedService = (Array.isArray(servicesList) ? servicesList : []).find(
          (srv: any) => {
            const serviceNumber = srv.title
              ? String(srv.title).match(/^\d+/)?.[0]
              : null;
            const detailTitle = String(detail.title).trim();
            return serviceNumber === detailTitle || srv.title === detailTitle;
          }
        );
      }

      const matchedServiceId =
        matchedService?.id ||
        matchedService?._id ||
        matchedService?.service_id;
      const resolvedServiceId = matchedServiceId || normalizedServiceId;
      let serviceValue: any;
      if (resolvedServiceId) {
        serviceValue = String(resolvedServiceId);
      } else if (matchedService && matchedService.title) {
        const titleMatch = String(matchedService.title).match(/^\d+/);
        serviceValue = titleMatch ? parseInt(titleMatch[0], 10) : matchedService.title;
      } else if (detail?.title) {
        const parsedTitle = parseInt(detail.title, 10);
        serviceValue = !isNaN(parsedTitle) ? parsedTitle : detail.title;
      } else {
        serviceValue = detail?.service_id || 60;
      }

      const quantity = Number(detail?.quantity) || 1;
      const unitCost = Number(
        detail?.amount ?? detail?.unit_cost ?? detail?.unitCost ?? detail?.price
      ) || 0;
      const discount = Number(detail?.discount) || 0;
      const totalPriceRaw = Number(detail?.net_amount);
      const totalPrice = Number.isFinite(totalPriceRaw) && totalPriceRaw
        ? totalPriceRaw
        : computeInvoiceItemTotal(quantity, unitCost, discount);

      return {
        id: index + 1,
        service: serviceValue,
        serviceId: resolvedServiceId ? String(resolvedServiceId) : undefined,
        serviceLabel:
          detail?.title ||
          detail?.service_name ||
          detail?.service_label ||
          undefined,
        quantity,
        unitCost,
        discount,
        totalPrice,
      };
    });
  };
  const [dragConfirmState, setDragConfirmState] = useState<{
    resolve: (val: boolean) => void;
    fromBranch?: string;
    toBranch?: string;
    fromStart?: string;
    fromEnd?: string;
    toStart?: string;
    toEnd?: string;
  } | null>(null);

  const requestDragConfirmation = React.useCallback(
    (details: {
      fromBranch?: string;
      toBranch?: string;
      fromStart?: string;
      fromEnd?: string;
      toStart?: string;
      toEnd?: string;
    }) =>
      new Promise<boolean>((resolve) => {
        setDragConfirmState({ resolve, ...details });
      }),
    []
  );
  const timeStepMinutes = slotDuration === "00:15:00" ? 15 : 30;
  const timeOptions = React.useMemo(
    () => buildTimeOptions(timeStepMinutes),
    [timeStepMinutes]
  );
  const normalizeTimeValue = React.useCallback(
    (value: string) => {
      const matched = timeOptions.find((option) => option.value === value);
      if (matched) return matched.value;

      const [rawHour = "0", rawMinute = "0"] = String(value || "")
        .split(":")
        .map((part) => part.trim());
      const hour = Math.min(23, Math.max(0, parseInt(rawHour, 10) || 0));
      const parsedMinute = Math.max(0, parseInt(rawMinute, 10) || 0);
      const step = Math.max(1, timeStepMinutes || 30);
      const minute =
        Math.floor(Math.min(59, parsedMinute) / step) * step;

      return `${String(hour).padStart(2, "0")}:${String(minute).padStart(
        2,
        "0"
      )}`;
    },
    [timeOptions, timeStepMinutes]
  );

  const parseDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr) return null;
    try {
      const combined = `${dateStr} ${timeStr || ""}`.trim();
      const d = new Date(combined);
      if (!isNaN(d.getTime())) return d.toISOString();
    } catch {}
    try {
      const base = new Date(dateStr);
      if (!isNaN(base.getTime())) return base.toISOString();
    } catch {}
    return null;
  };

  const formatDateForPayload = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}/${m}/${day}`;
  };

  const formatTimeForPayload = (d: Date) => {
    return toTimeInput(d);
  };

  const timeStringToMinutes = (t: string) => {
    if (!t) return 0;
    const raw = String(t).trim();
    if (!raw) return 0;
    const ampmMatch = raw.match(
      /(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(AM|PM)/i
    );
    if (ampmMatch) {
      let hh = parseInt(ampmMatch[1] || "0", 10);
      const mm = parseInt(ampmMatch[2] || "0", 10) || 0;
      const ap = (ampmMatch[4] || "").toLowerCase();
      if (ap === "pm" && hh < 12) hh += 12;
      if (ap === "am" && hh === 12) hh = 0;
      if (isNaN(hh) || isNaN(mm)) return 0;
      return hh * 60 + mm;
    }
    const digitsOnly = (val: string) => val.replace(/\D+/g, "");
    const parts = raw.split(":");
    const hh = parseInt(digitsOnly(parts[0] || "0"), 10);
    const mm = parseInt(digitsOnly(parts[1] || "0"), 10);
    if (isNaN(hh) || isNaN(mm)) return 0;
    return hh * 60 + mm;
  };

  const clampEndTime = (
    startTime: string,
    desiredEndTime: string,
    maxMinutes = 120
  ) => {
    try {
      const maxDuration = Number(maxMinutes || 120);
      const desiredRaw = String(desiredEndTime || "").trim();
      const desiredDuration = desiredRaw
        ? minutesBetweenTimes(startTime, desiredRaw)
        : maxDuration;
      const finalDuration = Math.min(
        Math.max(desiredDuration || 0, 0),
        maxDuration
      );
      return addMinutesToTime(startTime, finalDuration);
    } catch (e) {
      return desiredEndTime;
    }
  };

  const minutesBetweenTimes = (startTime: string, endTime: string) => {
    const startMin = timeStringToMinutes(startTime);
    const endMin = timeStringToMinutes(endTime);
    const diff = endMin - startMin;
    return diff < 0 ? endMin + 24 * 60 - startMin : diff;
  };

  const isMidnightTimeValue = (value: any) => {
    if (value === null || value === undefined) return false;
    const trimmed = String(value).trim();
    if (!trimmed) return false;
    if (/^24:0{2}(:0{2})?$/.test(trimmed)) return true;
    return timeStringToMinutes(trimmed) === 0;
  };

  const formatTimeHHMM = (value: any) => {
    if (!value) return "";
    const parts = String(value).split(":");
    if (parts.length < 2) return String(value);
    const hh = String(parseInt(parts[0], 10) || 0).padStart(2, "0");
    const mm = String(parseInt(parts[1], 10) || 0).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const lastDateClickRef = React.useRef<{ time: number; iso: string | null }>({
    time: 0,
    iso: null,
  });

  const toDateInput = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const toTimeInput = (d: Date) => {
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const combineDateAndTime = (
    dateStr: string | undefined | null,
    timeStr: string | undefined | null
  ) => {
    if (!dateStr) return null;
    let datePart = String(dateStr).trim();
    datePart = datePart.replace(/\//g, "-");
    const dateMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      datePart = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
    }

    let t = timeStr ? String(timeStr).trim() : "00:00";
    let addDay = 0;
    const raw24Match = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (raw24Match) {
      let hh = parseInt(raw24Match[1], 10);
      const mm = parseInt(raw24Match[2] || "0", 10) || 0;
      const ss = parseInt(raw24Match[3] || "0", 10) || 0;
      if (hh >= 24) {
        addDay = Math.floor(hh / 24);
        hh = hh % 24;
        t = `${String(hh).padStart(2, "0")}:${String(mm).padStart(
          2,
          "0"
        )}:${String(ss).padStart(2, "0")}`;
      }
    }
    const ampm = t.match(/(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(AM|PM|am|pm)/);
    if (ampm) {
      try {
        let hh = parseInt(ampm[1], 10);
        const mm = parseInt(ampm[2] || "0", 10) || 0;
        const ss = parseInt(ampm[3] || "0", 10) || 0;
        const ap = (ampm[4] || "").toLowerCase();
        if (ap === "pm" && hh < 12) hh += 12;
        if (ap === "am" && hh === 12) hh = 0;
        t = `${String(hh).padStart(2, "0")}:${String(mm).padStart(
          2,
          "0"
        )}:${String(ss).padStart(2, "0")}`;
      } catch (e) {}
    } else {
      if (t.length === 5 && /^\d{1,2}:\d{2}$/.test(t)) t = `${t}:00`;
    }

    try {
      const combined = `${datePart}T${t}`;
      const d = new Date(combined);
      if (!isNaN(d.getTime())) {
        if (addDay) d.setDate(d.getDate() + addDay);
        return d;
      }
    } catch (e) {}

    try {
      const d = new Date(`${datePart} ${timeStr || ""}`);
      if (!isNaN(d.getTime())) return d;
    } catch (e) {}
    try {
      const d = new Date(datePart);
      if (!isNaN(d.getTime())) return d;
    } catch (e) {}
    return null;
  };

  const getStatusColors = (status: string) => {
    const statusLower = String(status || "").toLowerCase();

    if (statusLower === "confirmed") {
      return {
        backgroundColor: "#4caf50",
        borderColor: "#45a049",
        textColor: "white",
      };
    } else if (statusLower === "pending") {
      return {
        backgroundColor: "#2196F3",
        borderColor: "#1976D2",
        textColor: "white",
      };
    } else if (statusLower === "cancelled") {
      return {
        backgroundColor: "#f44336",
        borderColor: "#da190b",
        textColor: "white",
      };
    }

    return {
      backgroundColor: "#4caf50",
      borderColor: "#45a049",
      textColor: "white",
    };
  };

  const buildEventTooltip = (raw: any) => {
    if (!raw) return "";
    const parts: string[] = [];
    try {
      if (raw.start_date || raw.time_start) {
        const sd = raw.start_date || raw.date || "";
        const st = raw.time_start || raw.time || raw.start_time || "";
        parts.push(`Start: ${sd} ${st}`.trim());
      }
      if (raw.time_end) parts.push(`End: ${raw.time_end}`);
      if (raw.patient && raw.patient.name)
        parts.push(`Client: ${raw.patient.name}`);
      if (raw.client_name || raw.customer_name)
        parts.push(`Client: ${raw.client_name || raw.customer_name}`);
      if (raw.notes)
        parts.push(`Notes: ${String(raw.notes).replace(/\n/g, " ")}`);
      if (raw.services) {
        const svc = Array.isArray(raw.services)
          ? raw.services.join(", ")
          : String(raw.services);
        parts.push(`Services: ${svc}`);
      }
      if (raw.status) parts.push(`Status: ${raw.status}`);
    } catch (e) {}
    return parts.join(" | ");
  };

  const resolveClientName = (raw: any, fallbackTitle?: string): string => {
    const resolved =
      raw?.client_name ||
      raw?.clientName ||
      ((typeof raw?.client === "string" || typeof raw?.client === "number")
        ? raw?.client
        : null) ||
      raw?.customer_name ||
      raw?.customerName ||
      ((typeof raw?.customer === "string" || typeof raw?.customer === "number")
        ? raw?.customer
        : null) ||
      (raw?.customer && raw?.customer.name) ||
      (raw?.patient && raw?.patient.name) ||
      raw?.patient_name ||
      raw?.client?.name ||
      raw?.user_name ||
      raw?.user?.name ||
      raw?.name ||
      raw?.title ||
      fallbackTitle ||
      "";
    return String(resolved || "").trim();
  };

  const resolveBranchName = (raw: any): string => {
    const directBranch =
      raw?.branch_name ||
      raw?.branchName ||
      raw?.business_name ||
      raw?.businessName ||
      raw?.branch?.name ||
      raw?.business?.name;
    if (directBranch) return String(directBranch).trim();

    const rawBusinessId =
      raw?.branch_id ||
      raw?.branchId ||
      raw?.business_id ||
      raw?.businessId ||
      raw?.business;

    if (rawBusinessId && Array.isArray(branches)) {
      const matchedBranch = branches.find(
        (b: any) =>
          String(b.id) === String(rawBusinessId) ||
          String(b.business_id) === String(rawBusinessId)
      );
      if (matchedBranch) {
        return String(
          matchedBranch.name ||
            matchedBranch.title ||
            matchedBranch.branch_name ||
            matchedBranch.business_name ||
            matchedBranch.id
        ).trim();
      }
    }

    return "";
  };

  const resolveResourceId = (raw: any) => {
    const rawBusinessId =
      raw?.branch_id ||
      raw?.branchId ||
      raw?.business_id ||
      raw?.businessId ||
      raw?.business;

    const matched = (Array.isArray(branches) ? branches : []).find(
      (branch: any) =>
        String(branch.id) === String(rawBusinessId) ||
        String(branch.business_id) === String(rawBusinessId)
    );

    if (!matched && selectedBranchId !== "all") return String(selectedBranchId);

    return matched ? String(matched.id) : "";
  };

  const renderEventContent = (arg: any) => {
    try {
      const raw = arg.event.extendedProps?.raw || {};
      let timeText = arg.timeText || "";
      const rawStart = raw.time_start || raw.start_time || raw.time || "";
      const rawEnd = raw.time_end || raw.end_time || raw.end || "";
      if (rawStart && rawEnd && isMidnightTimeValue(rawEnd)) {
        const startMin = timeStringToMinutes(String(rawStart));
        if (startMin > 0) {
          const startLabel = formatTimeHHMM(rawStart);
          timeText = startLabel ? `${startLabel} - 12:00` : "12:00";
        }
      }
      const status = raw.status || arg.event.extendedProps?.status || "";
      const name =
        arg.event.extendedProps?.clientName ||
        resolveClientName(raw, arg.event.title) ||
        "Appointment";
      const branchName =
        arg.event.extendedProps?.branchName || resolveBranchName(raw);
      const metaParts: string[] = [];
      if (selectedBranchId === "all" && branchName) metaParts.push(branchName);
      if (status) metaParts.push(status);

      return (
        <div className="fc-custom-event" style={{ position: "relative" }}>
          <div
            className="fc-event-time"
            style={{ fontSize: "0.75rem", opacity: 0.9 }}
          >
            {timeText}
          </div>
          <div
            className="fc-event-main"
            style={{ fontSize: "0.5rem", fontWeight: 500 }}
          >
            {name}
          </div>
          {metaParts.length > 0 && (
            <div
              className="fc-event-status"
              style={{
                fontSize: "0.55rem",
                opacity: 0.85,
                marginTop: "0px",
              }}
            >
              {metaParts.join(" • ")}
            </div>
          )}
        </div>
      );
    } catch (e) {
      return null;
    }
  };

  const mapAppointmentToEvent = (appt: any) => {
    const startTimeValue =
      appt?.time_start ??
      appt?.start_time ??
      appt?.timeStart ??
      appt?.startTime ??
      "";
    const endTimeValue =
      appt?.time_end ??
      appt?.end_time ??
      appt?.timeEnd ??
      appt?.endTime ??
      "";
    const hasStartTime = String(startTimeValue).trim() !== "";
    const hasEndTime = String(endTimeValue).trim() !== "";
    const startDateObj = hasStartTime
      ? combineDateAndTime(appt?.start_date, startTimeValue)
      : null;
    const endDateObj = hasEndTime
      ? combineDateAndTime(appt?.start_date, endTimeValue)
      : null;
    const start =
      (startDateObj ? startDateObj.toISOString() : null) ||
      parseDateTime(appt?.start_date, startTimeValue) ||
      appt?.start_date;
    let end =
      (endDateObj ? endDateObj.toISOString() : null) ||
      parseDateTime(appt?.start_date, endTimeValue) ||
      appt?.end_date;

    if (startDateObj) {
      const startMin = timeStringToMinutes(String(startTimeValue || ""));
      const endMin = timeStringToMinutes(String(endTimeValue || ""));
      if (isMidnightTimeValue(endTimeValue) && startMin > 0) {
        const endOfDay = combineDateAndTime(appt?.start_date, "23:59:59");
        if (endOfDay) end = endOfDay.toISOString();
      } else if (endDateObj && endMin < startMin) {
        const adjustedEnd = new Date(endDateObj.getTime() + 24 * 60 * 60000);
        end = adjustedEnd.toISOString();
      }
    }
    const statusColors = getStatusColors(appt?.status);
    const clientName = resolveClientName(appt);
    const branchName = resolveBranchName(appt);
    const branchId = resolveResourceId(appt);
    const title =
      selectedBranchId === "all" && branchName
        ? `${clientName || "Appointment"} (${branchName})`
        : clientName || appt?.notes || "Appointment";

    return {
      id: String(
        appt?.id || appt?._id || `${appt?.start_date}_${startTimeValue}`
      ),
      title,
      start,
      end,
      backgroundColor: statusColors.backgroundColor,
      borderColor: statusColors.borderColor,
      textColor: statusColors.textColor,
      extendedProps: {
        status: appt?.status,
        raw: appt,
        clientName: clientName || "Appointment",
        branchName,
        branchId,
        timeStart: startTimeValue,
        timeEnd: endTimeValue,
      },
    };
  };

  const resolveSlotBranchId = (slot: any, fallbackBusinessId?: any) => {
    const direct = resolveResourceId(slot);
    if (direct) return direct;

    const nestedBranch = slot?.branch || slot?.business;
    if (nestedBranch) {
      const nestedResolved = resolveResourceId(nestedBranch);
      if (nestedResolved) return nestedResolved;
    }

    if (fallbackBusinessId !== undefined && fallbackBusinessId !== null) {
      const fallbackResolved = resolveResourceId({
        business_id: fallbackBusinessId,
      });
      if (fallbackResolved) return fallbackResolved;
      const fallbackStr = String(fallbackBusinessId);
      if (displayedBranchIds.some((id) => String(id) === fallbackStr)) {
        return fallbackStr;
      }
    }

    if (selectedBranchId !== "all") return String(selectedBranchId);

    return "";
  };

  const buildSlotEvents = (slotsData: any[], businessId: any) => {
    const slotEvents: any[] = [];

    (Array.isArray(slotsData) ? slotsData : []).forEach(
      (slot: any, idx: number) => {
        const slotDate =
          slot.date || slot.start_date || slot.slot_date || slot.day;
        const slotStart =
          slot.start_time || slot.time_start || slot.from || slot.start;
        const slotEnd = slot.end_time || slot.time_end || slot.to || slot.end;

        if (!slotDate || !slotStart || !slotEnd) return;

        const startDateObj = combineDateAndTime(slotDate, slotStart);
        const endDateObj = combineDateAndTime(slotDate, slotEnd);
        const startIso = startDateObj ? startDateObj.toISOString() : null;
        const endIso = endDateObj ? endDateObj.toISOString() : null;
        const branchId = resolveSlotBranchId(slot, businessId);

        if (!branchId) return;
        if (
          displayedBranchIds.length > 0 &&
          !displayedBranchIds.some((id) => String(id) === String(branchId))
        ) {
          return;
        }

        slotEvents.push({
          id: `slot_blocked_${businessId}_${branchId}_${idx}_${slotDate}_${slotStart}`,
          title: "Closed",
          start: startIso || `${slotDate}T${slotStart || "00:00"}:00`,
          end: endIso || `${slotDate}T${slotEnd || "00:00"}:00`,
          backgroundColor: "rgba(220, 53, 69, 0.9)",
          borderColor: "rgba(220, 53, 69, 1)",
          textColor: "white",
          display: "background",
          editable: false,
          overlap: true,
          extendedProps: { raw: slot, slot: true, blocked: true, branchId },
        });
      }
    );

    return slotEvents;
  };

  const lastDatesSetRef = React.useRef<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  const handleDatesSet = (arg: any) => {
    try {
      const startMs =
        arg && arg.start instanceof Date ? arg.start.getTime() : 0;
      const endMs = arg && arg.end instanceof Date ? arg.end.getTime() : 0;
      if (
        startMs &&
        endMs &&
        (lastDatesSetRef.current.start !== startMs ||
          lastDatesSetRef.current.end !== endMs)
      ) {
        lastDatesSetRef.current = { start: startMs, end: endMs };
        setVisibleStart(new Date(startMs));
        setVisibleEnd(new Date(endMs));
      }

      if (arg && arg.view && arg.view.type) {
        const viewType = arg.view.type;
        let mappedView: "day" | "week" | "month" = "week";
        if (viewType === "dayGridMonth") mappedView = "month";
        else if (viewType === "timeGridWeek") mappedView = "week";
        else if (viewType === "timeGridDay") mappedView = "day";
        setCurrentView((prev) => (prev === mappedView ? prev : mappedView));
      }
    } catch (e) {}
  };

  const formatDateTimeShort = (d: Date | null | undefined) => {
    if (!d) return "";
    try {
      const dt = d instanceof Date ? d : new Date(d as any);
      if (isNaN(dt.getTime())) return "";
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(dt.getDate()).padStart(2, "0")} ${String(
        dt.getHours()
      ).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
    } catch (e) {
      return "";
    }
  };

  const addMinutesToTime = (timeStr: string, minutesToAdd: number) => {
    if (!timeStr) return timeStr;
    const parts = timeStr.split(":");
    if (parts.length < 2) return timeStr;
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    if (isNaN(hh) || isNaN(mm)) return timeStr;
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    d.setMinutes(d.getMinutes() + Number(minutesToAdd || 0));
    return toTimeInput(d);
  };

  const findServiceDuration = (serviceId: string) => {
    if (!serviceId) return null;
    
    // Debug: log what we're searching for
    const availableServices = Array.isArray(services) ? services : [];
    
    const svcRaw = availableServices.find(
      (s: any) =>
        String(s.id || s._id || s.service_id || "") === String(serviceId)
    );
    
    const svc: any = svcRaw as any;
    if (!svc) {
      console.warn(`Service not found in memory: ${serviceId}. Available services:`, availableServices.map((s: any) => ({ id: s.id || s._id, name: s.name, title: s.title, duration: s.duration })));
      return null;
    }

    console.log(`Looking up service: ${serviceId}`, svc);
    
    // Strategy 1: Check for explicit duration field
    let val = svc.duration ?? svc.length ?? svc.minutes ?? null;
    if (val !== undefined && val !== null) {
      const num = Number(val);
      if (!isNaN(num) && num > 0) {
        console.log(`Found service duration (explicit field): ${serviceId} -> ${num} minutes`);
        return num;
      }
    }

    // Get the service name/title for parsing
    const text = String(
      svc.name || svc.title || svc.label || svc.service_name || ""
    ).trim();
    
    if (text) {
      console.log(`Service name/title: "${text}"`);

      // Strategy 2: Try to parse the entire name as a number first (e.g., "60" or "90")
      const plainNumber = Number(text);
      if (!isNaN(plainNumber) && plainNumber > 0) {
        console.log(`Found service duration (plain number): ${serviceId} -> ${plainNumber} minutes`);
        return plainNumber;
      }

      const textLower = text.toLowerCase();

      // Strategy 3: Try hour format (e.g., "1h", "1.5h", "2h")
      const hourMatch = textLower.match(/(\d+(?:[.,]\d+)?)\s*h(?:our)?s?/);
      if (hourMatch) {
        const asNum = Number(hourMatch[1].replace(",", "."));
        if (!isNaN(asNum) && asNum > 0) {
          const mins = Math.round(asNum * 60);
          console.log(`Found service duration (hour format): ${serviceId} -> ${mins} minutes (from ${asNum}h)`);
          return mins;
        }
      }

      // Strategy 4: Try minute format with "min", "mins", "m" suffix (e.g., "60m", "90min")
      const minuteMatch = textLower.match(/(\d+)\s*(?:m|min|mins|minutes)?/);
      if (minuteMatch) {
        const asNum = Number(minuteMatch[1]);
        if (!isNaN(asNum) && asNum > 0) {
          // Make sure this isn't matching a small number that's part of something else
          if (asNum >= 15) { // Only accept if 15 minutes or more (reasonable minimum)
            console.log(`Found service duration (minute format): ${serviceId} -> ${asNum} minutes`);
            return asNum;
          }
        }
      }

      // Strategy 5: Specific text patterns
      if (textLower.includes("1 hour") || textLower.includes("one hour") || textLower.includes("60m") || textLower.includes("60")) {
        console.log(`Found service duration (1 hour pattern): ${serviceId} -> 60 minutes`);
        return 60;
      }
      if (textLower.includes("1.5 hour") || textLower.includes("90") || textLower.includes("90m") || textLower.includes("1 hour 30") || textLower.includes("1h30")) {
        console.log(`Found service duration (1.5 hour pattern): ${serviceId} -> 90 minutes`);
        return 90;
      }
      if (textLower.includes("2 hour") || textLower.includes("two hour") || textLower.includes("120") || textLower.includes("120m") || textLower.includes("2h")) {
        console.log(`Found service duration (2 hour pattern): ${serviceId} -> 120 minutes`);
        return 120;
      }
      if (textLower.includes("30") || textLower.includes("30m")) {
        console.log(`Found service duration (30 min pattern): ${serviceId} -> 30 minutes`);
        return 30;
      }
      if (textLower.includes("45") || textLower.includes("45m")) {
        console.log(`Found service duration (45 min pattern): ${serviceId} -> 45 minutes`);
        return 45;
      }
    }

    console.warn(`Could not determine duration for service: ${serviceId}`, svc);
    return null;
  };

  // Async version that can fetch services if needed
  const findServiceDurationAsync = async (serviceId: string, branchId?: string): Promise<number | null> => {
    if (!serviceId) return null;
    
    console.log(`findServiceDurationAsync: Looking for service ${serviceId} in branch ${branchId}`);
    
    // First try with current services in memory
    const inMemoryResult = findServiceDuration(serviceId);
    if (inMemoryResult !== null) {
      return inMemoryResult;
    }
    
    // If not found and branchId provided, fetch services for that branch
    if (branchId) {
      try {
        console.log(`Service not in memory, fetching services for branch ${branchId}`);
        const { getBranchServices } = await import("../services/ServicesService");
        const fetchedServices = await getBranchServices(String(branchId));
        
        let serviceList: any[] = [];
        if (Array.isArray(fetchedServices)) {
          serviceList = fetchedServices;
        } else if (fetchedServices?.services && Array.isArray(fetchedServices.services)) {
          serviceList = fetchedServices.services;
        } else if (fetchedServices?.data && Array.isArray(fetchedServices.data)) {
          serviceList = fetchedServices.data;
        }
        
        console.log(`Fetched ${serviceList.length} services for branch ${branchId}`);
        
        // Try to find the service in fetched list
        const foundService = serviceList.find(
          (s: any) => String(s.id || s._id || s.service_id || "") === String(serviceId)
        );
        
        if (foundService) {
          console.log(`Found service in API response:`, foundService);
          
          // Try explicit duration first
          let val = foundService.duration ?? foundService.length ?? foundService.minutes ?? null;
          if (val !== undefined && val !== null) {
            const num = Number(val);
            if (!isNaN(num) && num > 0) {
              console.log(`Service duration from API (explicit): ${num} minutes`);
              return num;
            }
          }
          
          // Try parsing name as number
          const serviceName = String(foundService.name || foundService.title || "").trim();
          if (serviceName) {
            const plainNum = Number(serviceName);
            if (!isNaN(plainNum) && plainNum > 0) {
              console.log(`Service duration from API (name): ${plainNum} minutes`);
              return plainNum;
            }
          }
        } else {
          console.warn(`Service ${serviceId} not found in API response for branch ${branchId}`);
        }
      } catch (error) {
        console.error(`Error fetching services for branch ${branchId}:`, error);
      }
    }
    
    console.warn(`Could not determine duration for service ${serviceId} even after API fetch`);
    return null;
  };

  // Helper function to extract duration from appointment data
  const getAppointmentDuration = (raw: any): number | null => {
    // Try to get duration from service inside the appointment
    if (raw.services && Array.isArray(raw.services)) {
      for (const svc of raw.services) {
        if (typeof svc === "object") {
          // First try explicit duration field
          const duration = svc.duration ?? svc.minutes ?? svc.length;
          if (duration && !isNaN(Number(duration)) && Number(duration) > 0) {
            console.log("getAppointmentDuration: Found from service.duration:", duration);
            return Number(duration);
          }
          
          // Then try parsing from service name/title
          const serviceName = String(svc.name || svc.title || "").trim();
          if (serviceName) {
            const plainNum = Number(serviceName);
            if (!isNaN(plainNum) && plainNum > 0) {
              console.log("getAppointmentDuration: Found from service name:", plainNum);
              return plainNum;
            }
          }
        }
      }
    }
    
    // Try to get duration from service object
    if (raw.service && typeof raw.service === "object") {
      const duration = raw.service.duration ?? raw.service.minutes ?? raw.service.length;
      if (duration && !isNaN(Number(duration)) && Number(duration) > 0) {
        console.log("getAppointmentDuration: Found from service field:", duration);
        return Number(duration);
      }
      
      // Try parsing service name
      const serviceName = String(raw.service.name || raw.service.title || "").trim();
      if (serviceName) {
        const plainNum = Number(serviceName);
        if (!isNaN(plainNum) && plainNum > 0) {
          console.log("getAppointmentDuration: Found from service name:", plainNum);
          return plainNum;
        }
      }
    }
    
    // Try to calculate from existing start/end time
    const rawStartTime =
      raw.time_start ||
      raw.start_time ||
      raw.timeStart ||
      raw.startTime ||
      raw.time;
    const rawEndTime =
      raw.time_end || raw.end_time || raw.timeEnd || raw.endTime || raw.end;
    if (rawStartTime && rawEndTime) {
      try {
        const [startH, startM] = String(rawStartTime)
          .split(":")
          .map(Number);
        const [endH, endM] = String(rawEndTime).split(":").map(Number);
        
        if (!isNaN(startH) && !isNaN(startM) && !isNaN(endH) && !isNaN(endM)) {
          const startMin = startH * 60 + startM;
          const endMin = endH * 60 + endM;
          const duration = endMin > startMin ? endMin - startMin : (endMin + 1440) - startMin;
          
          if (duration > 0) {
            console.log("getAppointmentDuration: Calculated from times:", duration);
            return duration;
          }
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    return null;
  };

  const isClosedSlotAt = (date: Date, branchId?: string) => {
    const targetMs = date.getTime();
    const branchKey = branchId ? String(branchId) : "";
    return (Array.isArray(events) ? events : []).some((ev: any) => {
      const raw = ev.extendedProps?.raw || {};
      const eventBranchId = ev.extendedProps?.branchId;
      if (branchKey && (!eventBranchId || String(eventBranchId) !== branchKey))
        return false;
      const isClosed =
        ev.title === "Closed" ||
        ev.extendedProps?.blocked ||
        ev.extendedProps?.slot ||
        raw.blocked ||
        raw.slot ||
        raw.closed ||
        raw.is_blocked ||
        raw.closed === 1 ||
        raw.is_blocked === 1;
      if (!isClosed) return false;

      const startVal = ev.start;
      const endVal = ev.end;
      const startDate =
        startVal instanceof Date ? startVal : startVal ? new Date(startVal) : null;
      if (!startDate || isNaN(startDate.getTime())) return false;
      const endDate =
        endVal instanceof Date
          ? endVal
          : endVal
          ? new Date(endVal)
          : new Date(startDate.getTime() + 60 * 60000);
      const startMs = startDate.getTime();
      const endMs = endDate.getTime();
      return targetMs >= startMs && targetMs < endMs;
    });
  };

  const isClosedSlotEvent = (ev: any) => {
    if (!ev) return false;
    const raw = ev.extendedProps?.raw || {};
    return (
      ev.title === "Closed" ||
      ev.extendedProps?.blocked ||
      ev.extendedProps?.slot ||
      raw.blocked ||
      raw.slot ||
      raw.closed ||
      raw.is_blocked ||
      raw.closed === 1 ||
      raw.is_blocked === 1
    );
  };

  const openCreateDialogForDate = (clicked: Date, branchIdForForm: string) => {
    const mins = clicked.getMinutes();
    const rounded = Math.floor(mins / 15) * 15;
    const start = new Date(clicked);
    start.setMinutes(rounded);
    try {
      if (start.getTime() < new Date().getTime()) {
        toast.error(
          "Cannot create an appointment in the past. Please choose a current or future time."
        );
        lastDateClickRef.current = { time: 0, iso: null };
        return;
      }
    } catch (e) {}

    if (isClosedSlotAt(start, branchIdForForm)) {
      toast.error("This time is closed by admin.");
      lastDateClickRef.current = { time: 0, iso: null };
      return;
    }

    setFormDate(toDateInput(clicked));
    setFormBranchId(branchIdForForm);

    // Fetch branch-specific services
    const fetchBranchSpecificServices = async () => {
      try {
        const response = await getBranchServices(branchIdForForm);
        let servicesData = [];
        if (response && response.services) {
          servicesData = response.services;
        } else if (response && response.data && response.data.services) {
          servicesData = response.data.services;
        } else if (response && response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (Array.isArray(response)) {
          servicesData = response;
        }
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (error) {
        console.error("Error fetching branch services:", error);
      }
    };
    fetchBranchSpecificServices();

    const end = new Date(start.getTime() + 60 * 60000);
    setFormTimeStart(toTimeInput(start));
    setFormTimeEnd(toTimeInput(end));
    setBlockDate(toDateInput(clicked));
    setBlockStart(toTimeInput(start));
    setBlockEnd(toTimeInput(end));
    setBlockAction("close");
    try {
      if (formServiceId) {
        const dur = findServiceDuration(formServiceId);
        if (dur) {
          const desired = addMinutesToTime(toTimeInput(start), dur);
          setFormTimeEnd(clampEndTime(toTimeInput(start), desired, 120));
        }
      }
    } catch (e) {}
    setFormNotes("");
    setFormCustomerName("");
    setFormClientId("");
    setFormServiceId("");
    setFormStatus("Pending");
    setEditingEventId(null);
    setEditingEventRaw(null);
    setEditingPastAppointment(false);
    setAppointmentDialogTab(0);
    setAddDialogOpen(true);
    lastDateClickRef.current = { time: 0, iso: null };
  };

  const handleDateClick = (info: any, branchOverride?: string) => {
    const branchIdForForm =
      branchOverride && String(branchOverride).trim() !== ""
        ? String(branchOverride)
        : selectedBranchId !== "all"
        ? selectedBranchId
        : "";

    if (!branchIdForForm) {
      toast.warning(
        "Please select a specific branch before adding an appointment"
      );
      return;
    }
    const clicked: Date = info.date;
    if (isMobileInteraction) {
      openCreateDialogForDate(clicked, branchIdForForm);
      return;
    }
    const iso = clicked.toISOString();
    const now = Date.now();
    const prev = lastDateClickRef.current;
    if (prev.iso === iso && now - prev.time < 350) {
      openCreateDialogForDate(clicked, branchIdForForm);
    } else {
      lastDateClickRef.current = { time: now, iso };
    }
  };

  const lastEventClickRef = React.useRef<{ time: number; id: string | null }>({
    time: 0,
    id: null,
  });

  const openClosedSlotDialog = (ev: any) => {
    const raw = ev.extendedProps?.raw || {};
    const startD = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endD =
      ev.end instanceof Date
        ? ev.end
        : new Date(ev.end || startD.getTime() + 60 * 60000);
    const safeStart = isNaN(startD.getTime()) ? new Date() : startD;
    const safeEnd = isNaN(endD.getTime())
      ? new Date(safeStart.getTime() + 60 * 60000)
      : endD;
    const rawBranchId =
      ev.extendedProps?.branchId ||
      raw.branch_id ||
      raw.branchId ||
      raw.business_id ||
      raw.businessId;
    const branchIdStr = rawBranchId ? String(rawBranchId) : "";
    if (branchIdStr) {
      setFormBranchId(branchIdStr);
      if (selectedBranchId !== "all") {
        setSelectedBranchId(branchIdStr);
      }
    } else {
      setFormBranchId(selectedBranchId || "all");
    }
    setBlockDate(toDateInput(safeStart));
    setBlockStart(toTimeInput(safeStart));
    setBlockEnd(toTimeInput(safeEnd));
    setBlockAction("open");
    setEditingEventId(null);
    setEditingEventRaw(null);
    setEditingPastAppointment(false);
    setAppointmentDialogTab(1);
    setAddDialogOpen(true);
  };

  const openEditDialogForEvent = async (ev: any, id: string) => {
    const raw = ev.extendedProps?.raw || {};

    const startD = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endD =
      ev.end instanceof Date
        ? ev.end
        : new Date(ev.end || new Date(startD.getTime() + 60 * 60000));
    try {
      const isPast = endD.getTime() < Date.now();
      setEditingPastAppointment(isPast);
    } catch (e) {
      setEditingPastAppointment(false);
    }
    setEditingEventRaw(raw);
    setFormDate(toDateInput(startD));
    setFormTimeStart(toTimeInput(startD));
    setFormTimeEnd(toTimeInput(endD));
    setFormClientId(
      String(raw.client_id || raw.clientId || raw.client || "")
    );
    const svcVal =
      raw.services && raw.services[0]
        ? typeof raw.services[0] === "object"
          ? raw.services[0].id || raw.services[0]._id || raw.services[0]
          : raw.services[0]
        : raw.service_id || raw.service || "";
    setFormServiceId(String(svcVal || ""));
    try {
      const dur = findServiceDuration(String(svcVal || ""));
      if (dur) setFormTimeEnd(addMinutesToTime(toTimeInput(startD), dur));
    } catch (e) {}
    setFormNotes(raw.notes || raw.text || "");
    setFormCustomerName((raw.patient && raw.patient.name) || "");
    setFormStatus(raw.status || raw.state || "Pending");
    try {
      const rawBusinessId =
        raw.business_id || raw.businessId || raw.business || null;
      let foundBranch = null;
      if (Array.isArray(branches)) {
        foundBranch = branches.find(
          (b: any) =>
            String(b.business_id) === String(rawBusinessId) ||
            String(b.id) === String(rawBusinessId)
        );
      }
      if (foundBranch) {
        const branchId = String(foundBranch.id);
        setFormBranchId(branchId);
        if (selectedBranchId !== "all") {
          setSelectedBranchId(branchId);
        }

        // Fetch branch-specific services
        try {
          const response = await getBranchServices(branchId);
          let servicesData = [];
          if (response && response.services) {
            servicesData = response.services;
          } else if (response && response.data && response.data.services) {
            servicesData = response.data.services;
          } else if (response && response.data && Array.isArray(response.data)) {
            servicesData = response.data;
          } else if (Array.isArray(response)) {
            servicesData = response;
          }
          setServices(Array.isArray(servicesData) ? servicesData : []);
        } catch (error) {
          console.error("Error fetching branch services:", error);
        }
      } else {
        setFormBranchId(selectedBranchId || "all");
      }
    } catch (e) {
      setFormBranchId(selectedBranchId || "all");
    }
    setEditingEventId(id);
    setAppointmentDialogTab(0);
    setAddDialogOpen(true);
    lastEventClickRef.current = { time: 0, id: null };
  };

  const handleEventClick = async (info: any) => {
    const ev = info.event;
    const id = String(ev.id);
    const now = Date.now();
    const prev = lastEventClickRef.current;
    if (isClosedSlotEvent(ev)) {
      if (!isMobileInteraction && prev.id === id && now - prev.time < 350) {
        return;
      }
      if (!isMobileInteraction) {
        lastEventClickRef.current = { time: now, id };
      } else {
        lastEventClickRef.current = { time: 0, id: null };
      }
      openClosedSlotDialog(ev);
      return;
    }
    if (isMobileInteraction) {
      await openEditDialogForEvent(ev, id);
      return;
    }
    if (prev.id === id && now - prev.time < 350) {
      await openEditDialogForEvent(ev, id);
    } else {
      lastEventClickRef.current = { time: now, id };
    }
  };

  const handleEventDrop = async (info: any) => {
    const ev = info.event;
    const id = String(ev.id);
    const raw = ev.extendedProps?.raw || {};

    const startD = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endD =
      ev.end instanceof Date
        ? ev.end
        : new Date(
            (ev.end || new Date(startD.getTime() + 60 * 60000)).getTime()
          );

    try {
      const now = new Date();
      if (endD.getTime() < now.getTime()) {
        toast.error(
          "Cannot move appointments that already ended. Move reverted."
        );
        if (info && typeof info.revert === "function") info.revert();
        return;
      }
    } catch (e) {}

    const startStr = formatTimeForPayload(startD);
    const endStr = formatTimeForPayload(endD);
    const diff = minutesBetweenTimes(startStr, endStr);
    if (diff > 120) {
      toast.error("Maximum appointment duration is 2 hours. Move reverted.");
      if (info && typeof info.revert === "function") info.revert();
      return;
    }

    const businessId =
      raw.business_id ||
      raw.businessId ||
      (branches && branches[0] && (branches[0].business_id || branches[0].id));
    if (!businessId) {
      console.error("No business id for dropped event");
      info.revert();
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload: any = {
      all_day: 0,
      business_id: businessId,
      client_id: raw.client_id || raw.client || null,
      services:
        raw.services && Array.isArray(raw.services)
          ? raw.services
          : raw.service
          ? [raw.service]
          : raw.service_id
          ? [raw.service_id]
          : [],
      flag: raw.flag || "normal",
      notes: raw.notes || raw.text || "",
      number: raw.number || 1,
      servicePrice: raw.servicePrice || raw.price || "0",
      start_date: formatDateForPayload(startD),
      status: raw.status || raw.state || "Pending",
      time_end: formatTimeForPayload(endD),
      time_start: formatTimeForPayload(startD),
      user_id: raw.user_id || user.id || user._id,
    };

    try {
      const confirmMove = window.confirm(
        "Do you want to move this appointment to the new time?"
      );
      if (!confirmMove) {
        if (info && typeof info.revert === "function") info.revert();
        return;
      }

      await updateAppointment(id, payload);
      try {
        const resp2 = await getAllAppointments(businessId, {
          viewType: currentView,
        });
        let appointments: any[] = [];
        if (!resp2) appointments = [];
        else if (resp2.appointments) appointments = resp2.appointments;
        else if (resp2.data) appointments = resp2.data;
        else if (Array.isArray(resp2)) appointments = resp2;

        const mapped = appointments.map(mapAppointmentToEvent);

        try {
          const slotResp = await getSlots({
            business_id: businessId,
            start_date: formatDateForPayload(visibleStart).replace(/\//g, "-"),
            end_date: formatDateForPayload(visibleEnd).replace(/\//g, "-"),
          });
          let slotsData: any[] = [];
          if (!slotResp) slotsData = [];
          else if (slotResp.history && Array.isArray(slotResp.history))
            slotsData = slotResp.history;
          else if (slotResp.slots) slotsData = slotResp.slots;
          else if (slotResp.data && Array.isArray(slotResp.data))
            slotsData = slotResp.data;
          else if (Array.isArray(slotResp)) slotsData = slotResp;

          const slotEvents = buildSlotEvents(slotsData, businessId);

          setEvents([...mapped, ...slotEvents]);
          // Force calendar re-mount after events update
          setCalendarKey(prev => prev + 1);
        } catch (slotErr) {
          console.warn("Failed to fetch slots after drop", slotErr);
          setEvents(mapped);
          // Force calendar re-mount even if slots fail
          setCalendarKey(prev => prev + 1);
        }
      } catch (e) {
        console.warn("Refresh after drop failed", e);
      }
    } catch (err) {
      console.error("Error updating appointment after drop", err);
      if (info && typeof info.revert === "function") info.revert();
    }
  };

  const handleEventResize = async (info: any) => {
    const ev = info.event;
    const id = String(ev.id);
    const raw = ev.extendedProps?.raw || {};

    const startD = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endD =
      ev.end instanceof Date
        ? ev.end
        : new Date(
            (ev.end || new Date(startD.getTime() + 60 * 60000)).getTime()
          );

    try {
      const now = new Date();
      if (endD.getTime() < now.getTime()) {
        toast.error(
          "Cannot resize appointments that already ended. Resize reverted."
        );
        if (info && typeof info.revert === "function") info.revert();
        return;
      }
    } catch (e) {}

    const startStr = formatTimeForPayload(startD);
    const endStr = formatTimeForPayload(endD);
    const diff = minutesBetweenTimes(startStr, endStr);
    if (diff > 120) {
      toast.error("Maximum appointment duration is 2 hours. Resize reverted.");
      if (info && typeof info.revert === "function") info.revert();
      return;
    }

    const businessId =
      raw.business_id ||
      raw.businessId ||
      (branches && branches[0] && (branches[0].business_id || branches[0].id));
    if (!businessId) {
      console.error("No business id for resized event");
      info.revert();
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload: any = {
      all_day: 0,
      business_id: businessId,
      client_id: raw.client_id || raw.client || null,
      services:
        raw.services && Array.isArray(raw.services)
          ? raw.services
          : raw.service
          ? [raw.service]
          : raw.service_id
          ? [raw.service_id]
          : [],
      flag: raw.flag || "normal",
      notes: raw.notes || raw.text || "",
      number: raw.number || 1,
      servicePrice: raw.servicePrice || raw.price || "0",
      start_date: formatDateForPayload(startD),
      status: raw.status || raw.state || "Pending",
      time_end: formatTimeForPayload(endD),
      time_start: formatTimeForPayload(startD),
      user_id: raw.user_id || user.id || user._id,
    };

    try {
      await updateAppointment(id, payload);
      try {
        const resp2 = await getAllAppointments(businessId, {
          viewType: currentView,
        });
        let appointments: any[] = [];
        if (!resp2) appointments = [];
        else if (resp2.appointments) appointments = resp2.appointments;
        else if (resp2.data) appointments = resp2.data;
        else if (Array.isArray(resp2)) appointments = resp2;

        const mapped = appointments.map(mapAppointmentToEvent);
        try {
          const slotResp = await getSlots({
            business_id: businessId,
            start_date: formatDateForPayload(visibleStart).replace(/\//g, "-"),
            end_date: formatDateForPayload(visibleEnd).replace(/\//g, "-"),
          });
          let slotsData: any[] = [];
          if (!slotResp) slotsData = [];
          else if (slotResp.history && Array.isArray(slotResp.history))
            slotsData = slotResp.history;
          else if (slotResp.slots) slotsData = slotResp.slots;
          else if (slotResp.data && Array.isArray(slotResp.data))
            slotsData = slotResp.data;
          else if (Array.isArray(slotResp)) slotsData = slotResp;

          const slotEvents = buildSlotEvents(slotsData, businessId);

          setEvents([...mapped, ...slotEvents]);
          // Force calendar re-mount after events update
          setCalendarKey(prev => prev + 1);
        } catch (slotErr) {
          console.warn("Failed to fetch slots after resize", slotErr);
          setEvents(mapped);
          // Force calendar re-mount even if slots fail
          setCalendarKey(prev => prev + 1);
        }
      } catch (e) {
        console.warn("Refresh after resize failed", e);
      }
    } catch (err) {
      console.error("Error updating appointment after resize", err);
      if (info && typeof info.revert === "function") info.revert();
    }
  };

  useEffect(() => {
    let isActive = true;

    const fetchSlotDurationPreference = async () => {
      if (!venueId) {
        setSlotDuration("00:30:00");
        setSlotDurationLoading(false);
        return;
      }

      setSlotDurationLoading(true);
      try {
        const has15MinSlots = await hasFifteenMinuteSlots(venueId);
        if (!isActive) return;
        setSlotDuration(has15MinSlots ? "00:15:00" : "00:30:00");
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to fetch slot duration preference", error);
        setSlotDuration("00:30:00");
      } finally {
        if (isActive) setSlotDurationLoading(false);
      }
    };

    fetchSlotDurationPreference();

    return () => {
      isActive = false;
    };
  }, [venueId]);

  useEffect(() => {
    // sync form branch id with selected branch, unless we're editing a specific appointment
    if (!editingEventId) setFormBranchId(selectedBranchId || "all");
  }, [selectedBranchId, editingEventId]);

  useEffect(() => {
    if (currentView === "day") return;
    if (selectedBranchId === "all" && branches.length > 0) {
      setSelectedBranchId(String(branches[0].id));
    }
  }, [currentView, selectedBranchId, branches]);

  useEffect(() => {
    const normalizeDate = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let start = normalizeDate(activeDate);
    let end = normalizeDate(activeDate);

    if (currentView === "week") {
      const weekStart = normalizeDate(activeDate);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      start = weekStart;
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      end = weekEnd;
    } else if (currentView === "month") {
      const year = activeDate.getFullYear();
      const month = activeDate.getMonth();
      start = new Date(year, month, 1);
      end = new Date(year, month + 1, 0);
    }

    setVisibleStart(start);
    setVisibleEnd(end);
  }, [activeDate, currentView]);

  useEffect(() => {
    const calendars = Object.values(branchCalendarRefs.current);
    calendars.forEach((calendar) => {
      const api = calendar?.getApi?.();
      if (!api) return;
      if (api.view && api.view.type !== viewType) {
        api.changeView(viewType);
      }
      api.gotoDate(activeDate);
    });
  }, [activeDate, viewType, displayedBranchKey]);

  useEffect(() => {
    const root = schedulerWrapperRef.current;
    if (!root) return;
    const scrollers = Array.from(
      root.querySelectorAll(".fc-timegrid-body .fc-scroller")
    ) as HTMLElement[];
    if (scrollers.length < 1) return;
    let isSyncing = false;
    const onScroll = (event: Event) => {
      if (isSyncing) return;
      isSyncing = true;
      const target = event.target as HTMLElement;
      scrollers.forEach((scroller) => {
        if (scroller !== target) scroller.scrollTop = target.scrollTop;
      });
      isSyncing = false;
    };
    scrollers.forEach((scroller) =>
      scroller.addEventListener("scroll", onScroll)
    );

    let wheelTargets: HTMLElement[] = [];
    let wheelHandler: ((event: WheelEvent) => void) | null = null;

    if (!isSingleBranch) {
      const axisScroller = root.querySelector(
        ".branch-col.axis-owner .fc-timegrid-body .fc-scroller"
      ) as HTMLElement | null;
      if (axisScroller) {
        wheelTargets = Array.from(
          root.querySelectorAll(
            ".branch-col:not(.axis-owner) .fc-timegrid-body .fc-scroller"
          )
        ) as HTMLElement[];
        const handleWheel = (event: WheelEvent) => {
          if (!axisScroller) return;
          event.preventDefault();
          axisScroller.scrollTop += event.deltaY;
        };
        wheelTargets.forEach((scroller) =>
          scroller.addEventListener("wheel", handleWheel, { passive: false })
        );
        wheelHandler = handleWheel;
      }
    }

    return () => {
      scrollers.forEach((scroller) =>
        scroller.removeEventListener("scroll", onScroll)
      );
      if (wheelHandler) {
        const handler = wheelHandler;
        wheelTargets.forEach((scroller) =>
          scroller.removeEventListener("wheel", handler)
        );
      }
    };
  }, [currentView, displayedBranchKey, calendarKey, isSingleBranch]);

  useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        if (!venueId) {
          setBranches([]);
          return;
        }
        const response = await getAllBranches(venueId);
        let branchesData: any[] = [];
        if (response && response.businesses) branchesData = response.businesses;
        else if (response && response.data) branchesData = response.data;
        else if (Array.isArray(response)) branchesData = response;

        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (err) {
        console.error("Error fetching branches:", err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };
    fetchBranches();
  }, [venueId]);

  const fetchOrgClients = React.useCallback(
    async (preferredClient?: Client) => {
      try {
        const response = await getOrgClients();
        const list = Array.isArray(response?.users)
          ? response.users
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];
        let normalizedClients = list
          .map((c: any) => normalizeClientRecord(c))
          .filter((c: Client) => c.id);
        if (preferredClient?.id) {
          const exists = normalizedClients.some(
            (c: Client) => String(c.id) === String(preferredClient.id)
          );
          if (!exists) {
            normalizedClients = [preferredClient, ...normalizedClients];
          }
        }
        setClients({ users: normalizedClients });
      } catch (error) {
        console.error("Error fetching organization clients:", error);
      }
    },
    []
  );

  useEffect(() => {
    fetchOrgClients();
  }, [fetchOrgClients, venueId]);

  const resolveClientFromResponse = (response: any): Client | null => {
    if (!response) return null;
    const candidate =
      response.user || response.client || response.data || response;
    const normalized = normalizeClientRecord(candidate);
    return normalized.id ? normalized : null;
  };

  const openClientEditDialog = async (clientId?: string) => {
    const id = String(clientId || "").trim();
    if (!id) {
      toast.error("No client selected.");
      return;
    }
    const existing =
      (clients?.users || []).find(
        (c) => String(c.id || c._id || "") === id
      ) || null;
    if (existing) {
      setEditingClient(existing);
      setClientEditOpen(true);
      return;
    }
    setClientEditLoading(true);
    try {
      const response = await getClientById(id);
      const resolved = resolveClientFromResponse(response);
      if (!resolved) {
        toast.error("Client not found.");
        return;
      }
      setEditingClient(resolved);
      setClientEditOpen(true);
    } catch (error) {
      console.error("Failed to load client details:", error);
      toast.error("Failed to load client details.");
    } finally {
      setClientEditLoading(false);
    }
  };

  const resolveClientBusinessId = () => {
    const raw = editingEventRaw || {};
    const rawBusinessId =
      raw.business_id ||
      raw.businessId ||
      raw.branch_id ||
      raw.branchId ||
      raw.business ||
      null;
    if (rawBusinessId) return String(rawBusinessId);
    const branchId =
      formBranchId && formBranchId !== "all"
        ? String(formBranchId)
        : selectedBranchId !== "all"
        ? String(selectedBranchId)
        : "";
    if (!branchId) return "";
    const branch = branches.find(
      (b) =>
        String(b.id) === branchId ||
        String(b.business_id || "") === branchId
    );
    return String(branch?.business_id || branch?.id || branchId || "");
  };

  const handleClientEditSubmit = async (values: Record<string, any>) => {
    if (!editingClient) return;
    setClientEditLoading(true);
    try {
      const userId = getUserId() || "";
      const businessId = resolveClientBusinessId();
      const payload: any = {
        name: values.fullName || editingClient.name || "",
        email: values.email || editingClient.email || "",
        mobile: values.phoneNumber || editingClient.mobile || "",
        country_code: normalizeClientCountryCode(values.countryCode),
        gender: normalizeClientGenderValue(values.gender),
        dob: formatClientDateOnly(values.dob),
        user_id: userId,
        admin_user_id: userId,
      };
      if (businessId) {
        payload.business_id = businessId;
      }
      await updateClient(String(editingClient.id), payload);
      toast.success("Client updated successfully!");
      const updatedClient: Client = {
        ...editingClient,
        name: payload.name,
        email: payload.email,
        mobile: payload.mobile,
        phone: payload.mobile,
        country_code: payload.country_code,
        gender: payload.gender,
        dob: payload.dob,
      };
      setClients((prev) => {
        const list = prev?.users || [];
        const next = list.map((c) =>
          String(c.id || c._id || "") === String(updatedClient.id)
            ? { ...c, ...updatedClient }
            : c
        );
        return { users: next };
      });
      if (String(formClientId) === String(updatedClient.id)) {
        setFormCustomerName(updatedClient.name || "");
      }
      setClientEditOpen(false);
      setEditingClient(null);
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Failed to update client.");
    } finally {
      setClientEditLoading(false);
    }
  };

  useEffect(() => {
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        if (!venueId) {
          console.error("No venue ID available for services");
          setServices([]);
          return;
        }
        let targetBranch;
        const branchList =
          Array.isArray(branches) && branches.length
            ? branches
            : branchesRef.current;
        if (selectedBranchId === "all" || !selectedBranchId) {
          targetBranch = branchList?.[0];
        } else {
          targetBranch =
            (branchList || []).find(
              (b) => String(b.id) === String(selectedBranchId)
            ) || branchList?.[0];
        }

        const branchId = targetBranch?.id;
        const businessId = targetBranch?.business_id;
        
        if (!businessId && !branchId) {
          console.error("No valid business ID or branch ID found for services");
          setServices([]);
          return;
        }

        // Use branch ID if available, otherwise use business ID
        const idToUse = branchId || businessId;
        const response = await getBranchServices(idToUse);
        console.log("Services fetched for branch:", idToUse, response);
        let servicesData = [];
        if (response && response.services) {
          servicesData = response.services;
        } else if (response && response.data && response.data.services) {
          servicesData = response.data.services;
        } else if (response && response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (Array.isArray(response)) {
          servicesData = response;
        }

        setServices(Array.isArray(servicesData) ? servicesData : []);

        const serviceOptions = (
          Array.isArray(servicesData) ? servicesData : []
        ).map((service) => ({
          key: service.id || service._id,
          label: service.name,
        }));
      } catch (error) {
        console.error("Error fetching services:", error);
        setServices([]);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, [venueId, selectedBranchId, branches]);

  useEffect(() => {
    if (selectedBranchId === "all") return;
    const branch = branches.find(
      (b) => String(b.id) === String(selectedBranchId)
    );
    if (branch && branch.calendar_embed) {
      setEmbedUrl(branch.calendar_embed);
    } else if (branch && branch.calendarId) {
      setEmbedUrl(
        `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(
          branch.calendarId
        )}&ctz=UTC`
      );
    }
  }, [selectedBranchId, branches, visibleStart, visibleEnd]);

  const createInvoiceForAppointment = async (
    appointmentId: string,
    businessId: string,
    clientId: string | null,
    servicePrice: number,
    notes: string,
    serviceId?: string | null
  ) => {
    const invoiceNumber = Math.floor(Date.now() / 1000);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const formattedAmount =
      typeof servicePrice === "number"
        ? servicePrice.toFixed(2)
        : String(servicePrice || "0.00");

    const now = new Date();
    const pad2 = (n: number) => String(n).padStart(2, "0");
    const formattedNow = `${now.getFullYear()}-${pad2(
      now.getMonth() + 1
    )}-${pad2(now.getDate())} ${pad2(now.getHours())}:${pad2(
      now.getMinutes()
    )}:${pad2(now.getSeconds())}`;

    const invoicePayload: any = {
      appointment_id: appointmentId,
      business_id: businessId,
      client_id: clientId,
      created_by: user.id || user._id || clientId || businessId,
      discount: 0,
      id: 1,
      invoice_number: invoiceNumber,
      net_amount: formattedAmount,
      Vat: 0,
      total_amount: formattedAmount,
      notes: notes || "",
      created_at: formattedNow,
      updated_at: formattedNow,
      payment_method_id: 2,
      status_id: 2,
      services: [
        {
          service_id: serviceId || formServiceId || null,
          quantity: 1,
          amount: formattedAmount,
          net_amount: formattedAmount,
          discount: 0,
          created_at: formattedNow,
          updated_at: formattedNow,
        },
      ],
    };
    try {
      await createInvoice(invoicePayload);
      console.log(
        "Invoice created successfully for appointment",
        appointmentId
      );
    } catch (invoiceErr) {
      console.error("Error creating invoice", invoiceErr);
    }
  };

  const exportSlotsToWhatsApp = async () => {
    if (!branches || branches.length === 0) {
      toast.error("No branches available to export slots from");
      return;
    }

    const exportDateRaw =
      activeDate instanceof Date ? activeDate : new Date(activeDate);
    const safeExportDate = !isNaN(exportDateRaw.getTime())
      ? exportDateRaw
      : new Date();
    const exportDateKey = toDateInput(safeExportDate);
    const today = new Date();
    const isToday = exportDateKey === toDateInput(today);
    const minStartMin = isToday
      ? today.getHours() * 60 + today.getMinutes()
      : 0;

    const start = formatDateForPayload(safeExportDate).replace(/\//g, "-");
    const end = start;

    const parseSlotsData = (slotResp: any): any[] => {
      let slotsData: any[] = [];
      if (!slotResp) slotsData = [];
      else if (slotResp.history && Array.isArray(slotResp.history))
        slotsData = slotResp.history;
      else if (slotResp.slots) slotsData = slotResp.slots;
      else if (slotResp.data && Array.isArray(slotResp.data))
        slotsData = slotResp.data;
      else if (Array.isArray(slotResp)) slotsData = slotResp;
      return slotsData;
    };

    const getBranchLabel = (branch: any) => {
      const label =
        branch?.name ||
        branch?.title ||
        branch?.branch_name ||
        branch?.business_name ||
        branch?.id ||
        "Branch";
      return String(label).trim();
    };

    const normalizeTime = (timeStr: string) => {
      if (!timeStr) return null;
      const t = String(timeStr).trim();
      const parts = t.split(":");
      if (parts.length < 2) return null;
      const hh = parseInt(parts[0], 10) || 0;
      const mm = parseInt(parts[1], 10) || 0;
      return hh * 60 + mm;
    };

    const formatTime12Hour = (minutes: number) => {
      const fullDay = 24 * 60;
      const normalized = ((minutes % fullDay) + fullDay) % fullDay;
      const hh = Math.floor(normalized / 60);
      const mm = normalized % 60;
      const period = hh >= 12 ? "pm" : "am";
      const displayHh = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
      return `${displayHh}:${String(mm).padStart(2, "0")} ${period}`;
    };

    const generateSlots = (
      availablePeriods: Array<{ startMin: number; endMin: number }>,
      granularity = 30,
      minStart = 0
    ) => {
      const slots: string[] = [];

      const formatRange = (s: number, e: number) => {
        const start = formatTime12Hour(s);
        const end = formatTime12Hour(e);
        return `${start} - ${end}`;
      };

      availablePeriods.forEach(({ startMin, endMin }) => {
        let cursor = startMin;
        const candidates: Array<{ s: number; e: number }> = [];

        while (cursor + granularity <= endMin) {
          const next = cursor + granularity;
          if (cursor >= minStart) candidates.push({ s: cursor, e: next });
          cursor = next;
        }

        // Merge contiguous candidates into larger ranges (e.g., two 30-min => 60-min)
        let i = 0;
        while (i < candidates.length) {
          let s = candidates[i].s;
          let e = candidates[i].e;
          i++;
          while (i < candidates.length && candidates[i].s === e) {
            e = candidates[i].e;
            i++;
          }
          // push merged range
          slots.push(formatRange(s, e));
        }
      });

      return slots;
    };

    const buildClosedSlots = (branchId?: string) => {
      const closedSlots: Array<{ startMin: number; endMin: number }> = [];
      try {
        const toDateObj = (v: any) => (v instanceof Date ? v : new Date(v));
        const targetDateKey = exportDateKey;
        (Array.isArray(events) ? events : []).forEach((ev: any) => {
          try {
            if (branchId) {
              const eventBranchId =
                ev.extendedProps?.branchId ||
                resolveResourceId(ev.extendedProps?.raw || {});
              if (!eventBranchId) return;
              if (String(eventBranchId) !== String(branchId)) return;
            }

            const s = ev.start ? toDateObj(ev.start) : null;
            const e = ev.end ? toDateObj(ev.end) : null;
            if (!s) return;

            const sLocal = toDateInput(s);
            if (sLocal !== targetDateKey) return;

            const startMin = s.getHours() * 60 + s.getMinutes();
            const endDate = e || new Date(s.getTime() + 60 * 60000);
            let endMin = endDate.getHours() * 60 + endDate.getMinutes();
            if (endMin < startMin) endMin = 24 * 60;

            if (!isNaN(startMin) && !isNaN(endMin)) {
              closedSlots.push({ startMin, endMin });
            }
          } catch (err) {}
        });
      } catch (e) {}
      return closedSlots;
    };

    const buildGroupedSlots = (
      slotsData: any[],
      occupiedIntervals: Array<{ startMin: number; endMin: number }>
    ) => {
      const grouped: Record<string, string[]> = {};
      (Array.isArray(slotsData) ? slotsData : []).forEach((s: any) => {
        const blocked = !!(
          s.blocked ||
          s.closed ||
          s.is_blocked ||
          s.blocked === true ||
          s.blocked === 1 ||
          s.closed === 1 ||
          s.is_blocked === 1
        );

        if (blocked) {
          console.log("Skipping blocked slot:", s);
          return;
        }

        const slotDate = s.date || s.start_date || s.slot_date || s.day || "";
        const slotStart =
          s.start_time || s.time_start || s.from || s.start || "";
        const slotEnd = s.end_time || s.time_end || s.to || s.end || "";
        if (!slotDate || !slotStart || !slotEnd) return;
        const slotStartMin = normalizeTime(slotStart);
        let slotEndMin = normalizeTime(slotEnd);
        if (slotStartMin === null || slotEndMin === null) return;
        if (slotEndMin <= slotStartMin && slotStartMin > 0) {
          slotEndMin = 24 * 60;
        }

        let overlaps = false;
        for (const occ of occupiedIntervals) {
          if (slotStartMin < occ.endMin && slotEndMin > occ.startMin) {
            overlaps = true;
            break;
          }
        }
        if (overlaps) return;

        if (!grouped[slotDate]) grouped[slotDate] = [];
        const availablePeriods = [
          { startMin: slotStartMin, endMin: slotEndMin },
        ];
        // prefer 15-minute granularity but merge contiguous slots into longer ranges
        grouped[slotDate].push(
          ...generateSlots(availablePeriods, 30, minStartMin)
        );
      });

      if (
        !Object.keys(grouped).length ||
        !grouped[start] ||
        grouped[start].length === 0
      ) {
        const merged: Array<{ startMin: number; endMin: number }> = [];
        occupiedIntervals
          .sort((a, b) => a.startMin - b.startMin)
          .forEach((iv) => {
            if (!merged.length) merged.push({ ...iv });
            else {
              const last = merged[merged.length - 1];
              if (iv.startMin <= last.endMin)
                last.endMin = Math.max(last.endMin, iv.endMin);
              else merged.push({ ...iv });
            }
          });

        const DAY_START = 8 * 60; // 08:00
        const DAY_END = 24 * 60; // 24:00
        const availablePeriods: Array<{ startMin: number; endMin: number }> =
          [];
        let cursor = DAY_START;

        for (const iv of merged) {
          if (cursor < iv.startMin) {
            availablePeriods.push({ startMin: cursor, endMin: iv.startMin });
          }
          cursor = Math.max(cursor, iv.endMin);
        }
        if (cursor < DAY_END) {
          availablePeriods.push({ startMin: cursor, endMin: DAY_END });
        }

        const fallbackSlots = generateSlots(availablePeriods, 30, minStartMin);
        if (fallbackSlots.length) {
          grouped[start] = fallbackSlots;
          console.log("Using fallback available slots:", fallbackSlots);
        }
      }

      return grouped;
    };

    const getBranchAvailability = async (
      branch: any,
      applyBranchFilter: boolean
    ) => {
      const businessId = branch?.business_id || branch?.id;
      if (!businessId) return { grouped: {} };
      let slotsData: any[] = [];
      try {
        const slotResp = await getSlots({
          business_id: businessId,
          start_date: start,
          end_date: end,
        });
        slotsData = parseSlotsData(slotResp);
      } catch (error) {
        console.error("Error fetching slots for branch:", branch?.id, error);
      }

      const branchId = branch?.id ? String(branch.id) : null;
      const occupiedIntervals = buildClosedSlots(
        applyBranchFilter ? branchId || undefined : undefined
      );

      const grouped = buildGroupedSlots(slotsData, occupiedIntervals);
      return { grouped };
    };

    let message = "🎾 Available Court Slots 🎾\n\n";
    let totalSlots = 0;

    if (selectedBranchId === "all") {
      for (const branch of branches) {
        const branchLabel = getBranchLabel(branch);
        const { grouped } = await getBranchAvailability(branch, true);
        message += `Branch: ${branchLabel}\n`;

        const entries = Object.entries(grouped).filter(
          ([, slots]) => slots.length > 0
        );

        if (!entries.length) {
          message += "   - No available slots\n\n";
          continue;
        }

        entries.forEach(([date, slots]) => {
          message += `📅 ${date}\n`;
          slots.forEach((slot) => {
            message += `   ✓ ${slot}\n`;
            totalSlots++;
          });
          message += `\n`;
        });
      }
    } else {
      const branch = branches.find(
        (bb) => String(bb.id) === String(selectedBranchId)
      );
      if (!branch) {
        toast.error("No business selected");
        return;
      }
      const { grouped } = await getBranchAvailability(branch, false);

      Object.entries(grouped).forEach(([date, slots]) => {
        if (slots.length > 0) {
          message += `📅 ${date}\n`;
          slots.forEach((slot) => {
            message += `   ✓ ${slot}\n`;
            totalSlots++;
          });
          message += `\n`;
        }
      });
    }

    if (totalSlots === 0) {
      toast.error(
        `No available slots for ${exportDateKey}. All times are either booked or closed.`
      );
      return;
    }

    message += ``;

    console.log("Exporting available slots:", message);

    const encodedMessage = encodeURIComponent(message);

    const hasNavigator = typeof navigator !== "undefined";
    const userAgent =
      hasNavigator && navigator.userAgent ? navigator.userAgent : "";
    const isMobile = /Android|iPhone|iPad|iPod|Windows Phone/i.test(userAgent);

    const targets = isMobile
      ? [
          `https://wa.me/?text=${encodedMessage}`,
          `https://api.whatsapp.com/send?text=${encodedMessage}`,
        ]
      : [
          `https://web.whatsapp.com/send?text=${encodedMessage}`,
          `https://api.whatsapp.com/send?text=${encodedMessage}`,
        ];

    // Skip native share dialog and go directly to WhatsApp
    let opened = false;
    targets.some((url) => {
      const newWindow = window.open(url, "_blank", "noopener,noreferrer");
      if (newWindow) {
        opened = true;
        return true;
      }
      return false;
    });

    if (!opened) {
      const canWriteClipboard =
        hasNavigator &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function";

      if (canWriteClipboard) {
        try {
          await navigator.clipboard.writeText(message);
          toast.success("Slots copied to clipboard. Paste them into WhatsApp.");
          return;
        } catch (err) {}
      }

      toast.error(
        `Unable to open WhatsApp automatically. Copy and share this message:\n\n${message}`
      );
    }
  };
  const refreshAppointments = async () => {
    let businessId: any = null;
    if (!branches || branches.length === 0) return;
    if (selectedBranchId === "all") {
      businessId = branches[0]?.business_id || branches[0]?.id;
    } else {
      const b = branches.find(
        (bb) => String(bb.id) === String(selectedBranchId)
      );
      businessId = b?.business_id || b?.id;
    }

    if (!businessId) {
      setEvents([]);
      return;
    }

    setLoadingAppointments(true);
    try {
      const resp = await getAllAppointments(
        selectedBranchId === "all" ? venueId : businessId,
        {
          viewType: currentView,
          isVenue: selectedBranchId === "all",
        }
      );

      let appointments: any[] = [];

      if (!resp) {
        appointments = [];
      } else if (resp.today && resp.all) {
        const allData = resp.all;
        if (allData.appointments) appointments = allData.appointments;
        else if (allData.data) appointments = allData.data;
        else if (Array.isArray(allData)) appointments = allData;

      } else if (resp.appointments) {
        appointments = resp.appointments;
      } else if (resp.data) {
        appointments = resp.data;
      } else if (Array.isArray(resp)) {
        appointments = resp;
      }

      const mapped = appointments.map(mapAppointmentToEvent);

      try {
        const startDateParam = formatDateForPayload(visibleStart).replace(
          /\//g,
          "-"
        );
        const endDateParam = formatDateForPayload(visibleEnd).replace(
          /\//g,
          "-"
        );
        let slotEvents: any[] = [];

        if (selectedBranchId === "all") {
          for (const branch of branches) {
            try {
              const branchBizId = branch?.business_id || branch?.id;
              if (!branchBizId) continue;
              const slotResp = await getSlots({
                business_id: branchBizId,
                start_date: startDateParam,
                end_date: endDateParam,
              });
              let slotsData: any[] = [];
              if (!slotResp) slotsData = [];
              else if (slotResp.history && Array.isArray(slotResp.history))
                slotsData = slotResp.history;
              else if (slotResp.slots) slotsData = slotResp.slots;
              else if (slotResp.data && Array.isArray(slotResp.data))
                slotsData = slotResp.data;
              else if (Array.isArray(slotResp)) slotsData = slotResp;

              const branchSlotEvents = buildSlotEvents(slotsData, branchBizId);
              slotEvents = [...slotEvents, ...branchSlotEvents];
            } catch (error) {
              console.error(
                "Error fetching slots for branch:",
                branch?.id,
                error
              );
            }
          }
        } else {
          const slotResp = await getSlots({
            business_id: businessId,
            start_date: startDateParam,
            end_date: endDateParam,
          });
          let slotsData: any[] = [];
          if (!slotResp) slotsData = [];
          else if (slotResp.history && Array.isArray(slotResp.history))
            slotsData = slotResp.history;
          else if (slotResp.slots) slotsData = slotResp.slots;
          else if (slotResp.data && Array.isArray(slotResp.data))
            slotsData = slotResp.data;
          else if (Array.isArray(slotResp)) slotsData = slotResp;

          slotEvents = buildSlotEvents(slotsData, businessId);
        }

        const allEvents = [...mapped, ...slotEvents];
        setEvents(allEvents);
      } catch (e) {
        console.warn("Failed to fetch/merge slots", e);
        setEvents(mapped);
      }
    } catch (err) {
      console.error(
        "Error fetching appointments for branch",
        businessId,
        err
      );
      setEvents([]);
    } finally {
      setLoadingAppointments(false);
    }
  };

  useEffect(() => {
    if (branches && branches.length) refreshAppointments();
  }, [
    selectedBranchId,
    branches,
    visibleStart,
    visibleEnd,
    currentView,
    venueId,
  ]);

  const handleBlockSlotSubmit = async (values: Record<string, any>) => {
    const branchIdForBlock =
      formBranchId && formBranchId !== "all" ? formBranchId : selectedBranchId;
    if (!branchIdForBlock || branchIdForBlock === "all") {
      toast.error("Select a branch before opening or closing slots.");
      return;
    }
    const branch = branches.find(
      (b) => String(b.id) === String(branchIdForBlock)
    );
    const businessId = branch?.business_id || branch?.id || null;
    if (!businessId) {
      toast.error("Select a branch before opening or closing slots.");
      return;
    }

    const action = (values.block_action || blockAction || "close") as
      | "open"
      | "close";
    const date = String(values.block_date || blockDate || "").trim();
    const startRaw = String(values.block_start || blockStart || "").trim();
    const endRaw = String(values.block_end || blockEnd || "").trim();

    if (!date || !startRaw || !endRaw) {
      toast.error("Please select a date and time range.");
      return;
    }

    const startTime = normalizeTimeValue(startRaw);
    const endTime = normalizeTimeValue(endRaw);

    try {
      const diff = minutesBetweenTimes(startTime, endTime);
      if (isNaN(diff) || diff <= 0) {
        toast.error("End time must be after start time");
        return;
      }
    } catch (e) {
      toast.error("Invalid time values");
      return;
    }

    const slotData: any = {
      action,
      date,
      start_time: startTime,
      end_time: endTime,
      business_id: businessId,
    };

    try {
      await manageSlots(slotData);
      console.log("Slot management successful:", slotData);
      // attempt to refresh local appointments after change
      try {
        if (businessId) {
          const resp2 = await getAllAppointments(businessId, {
            viewType: currentView,
          });
          let appointments: any[] = [];
          if (!resp2) appointments = [];
          else if (resp2.appointments) appointments = resp2.appointments;
          else if (resp2.data) appointments = resp2.data;
          else if (Array.isArray(resp2)) appointments = resp2;

          const mapped = appointments.map(mapAppointmentToEvent);

          try {
            const slotResp = await getSlots({
              business_id: businessId,
              start_date: formatDateForPayload(visibleStart).replace(/\//g, "-"),
              end_date: formatDateForPayload(visibleEnd).replace(/\//g, "-"),
            });
            let slotsData: any[] = [];
            if (!slotResp) slotsData = [];
            else if (slotResp.history && Array.isArray(slotResp.history))
              slotsData = slotResp.history;
            else if (slotResp.slots) slotsData = slotResp.slots;
            else if (slotResp.data && Array.isArray(slotResp.data))
              slotsData = slotResp.data;
            else if (Array.isArray(slotResp)) slotsData = slotResp;

            const slotEvents = buildSlotEvents(slotsData, businessId);

            setEvents([...mapped, ...slotEvents]);
            setCalendarKey((prev) => prev + 1);
          } catch (e) {
            console.warn("Failed to fetch slots after manageSlots", e);
            setEvents(mapped);
            setCalendarKey((prev) => prev + 1);
          }
          toast.success(
            `Slot ${action === "open" ? "opened" : "closed"} successfully. Reloading...`
          );
          window.location.reload();
        }
      } catch (e) {
        console.warn("Failed to refresh appointments after manageSlots", e);
      }
    } catch (error) {
      console.error("Error managing slots:", error);
      toast.error("Failed to update slots");
    } finally {
      setAddDialogOpen(false);
    }
  };

  const handleAppointmentSubmit = async (values: Record<string, any>) => {
    if (editingEventId && editingPastAppointment) {
      toast.error("Past appointments cannot be edited.");
      return;
    }
    let businessId: any = null;
    if (!branches || branches.length === 0) return;
    const branchToUseId =
      values.branch_id || formBranchId || selectedBranchId;
    if (!branchToUseId || branchToUseId === "all") {
      businessId = branches[0]?.business_id || branches[0]?.id;
    } else {
      const branchIdStr = String(branchToUseId);
      const b = branches.find(
        (bb) =>
          String(bb.id) === branchIdStr ||
          String(bb.business_id || "") === branchIdStr
      );
      businessId = b?.business_id || b?.id;
    }
    if (!businessId) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const d = new Date(values.date);
    const startDateStr = `${d.getFullYear()}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}`;

    try {
      const serviceSelected =
        values.service_id && String(values.service_id || "").trim() !== "";
      if (!serviceSelected) {
        toast.error(
          editingEventId
            ? "Please select a service before updating."
            : "Please select a service before creating."
        );
        return;
      }

      try {
        const now = new Date();
        if (editingEventId) {
          const endDt = combineDateAndTime(values.date, values.time_end);
          if (endDt && endDt.getTime() < now.getTime()) {
            alert(
              "Cannot update appointments that already ended. Please choose a current or future end time."
            );
            return;
          }
        } else {
          const startDt = combineDateAndTime(values.date, values.time_start);
          if (startDt && startDt.getTime() < now.getTime()) {
            alert(
              "Cannot create an appointment in the past. Please choose a current or future time."
            );
            return;
          }
        }
      } catch (e) {}

      const svcIdForSubmit =
        values.service_id !== undefined && values.service_id !== null
          ? values.service_id
          : formServiceId || "";
      const durForSubmit = findServiceDuration(String(svcIdForSubmit || ""));
      if (durForSubmit && values.time_start) {
        const desired = addMinutesToTime(
          String(values.time_start),
          durForSubmit
        );
        values.time_end = clampEndTime(
          String(values.time_start),
          desired,
          120
        );
      }
    } catch (e) {}

    try {
      const diff = minutesBetweenTimes(
        String(values.time_start),
        String(values.time_end)
      );
      if (isNaN(diff) || diff <= 0) {
        toast.error("End time must be after start time");
        return;
      }
      if (diff > 120) {
        toast.error("Maximum appointment duration is 2 hours");
        return;
      }
    } catch (e) {
      console.error("Time validation error", e);
      toast.error("Invalid time values");
      return;
    }

    const selectedService = services.find(
      (s) => String(s.id || s._id) === values.service_id
    );
    const servicePrice = selectedService ? selectedService.price || 0 : 0;
    const payload: any = {
      all_day: 0,
      business_id: businessId,
      client_id: values.client_id || null,
      services: values.service_id ? [values.service_id] : [],
      flag: "normal",
      notes: values.notes || "",
      number: 1,
      servicePrice: servicePrice,
      start_date: startDateStr,
      status: values.status || "Pending",
      time_end: values.time_end,
      time_start: values.time_start,
      user_id: user.id || user._id,
    };

    try {
      let appointmentId: string | null = null;
      if (editingEventId) {
        await updateAppointment(editingEventId.toString(), payload);
        toast.success("Appointment updated successfully!");
      } else {
        const resp = await createAppointment(payload);
        appointmentId = resp.appointment.id || resp._id;
        if (appointmentId) {
          await createInvoiceForAppointment(
            appointmentId,
            businessId,
            values.client_id || null,
            resp.servicePrice || servicePrice,
            values.notes || "",
            values.service_id || formServiceId || null
          );
        }
        toast.success("Appointment created successfully!");
      }

      // Fetch appointments based on current view
      let allAppointments: any[] = [];

      if (selectedBranchId === "all") {
        // Fetch appointments for all branches
        for (const branch of branches) {
          try {
            const branchBizId = branch.business_id || branch.id;
            const resp2 = await getAllAppointments(branchBizId, {
              viewType: currentView,
            });
            let appointments: any[] = [];
            if (!resp2) {
              appointments = [];
            } else if (resp2.today && resp2.all) {
              const allData = resp2.all;
              if (allData.appointments) appointments = allData.appointments;
              else if (allData.data) appointments = allData.data;
              else if (Array.isArray(allData)) appointments = allData;
            } else if (resp2.appointments) {
              appointments = resp2.appointments;
            } else if (resp2.data) {
              appointments = resp2.data;
            } else if (Array.isArray(resp2)) {
              appointments = resp2;
            }
            allAppointments = [...allAppointments, ...appointments];
          } catch (error) {
            console.error(
              "Error fetching appointments for branch:",
              branch.id,
              error
            );
          }
        }
      } else {
        // Fetch appointments for the selected branch only
        const resp2 = await getAllAppointments(businessId, {
          viewType: currentView,
        });
        let appointments: any[] = [];
        if (!resp2) {
          appointments = [];
        } else if (resp2.today && resp2.all) {
          const allData = resp2.all;
          if (allData.appointments) appointments = allData.appointments;
          else if (allData.data) appointments = allData.data;
          else if (Array.isArray(allData)) appointments = allData;
        } else if (resp2.appointments) {
          appointments = resp2.appointments;
        } else if (resp2.data) {
          appointments = resp2.data;
        } else if (Array.isArray(resp2)) {
          appointments = resp2;
        }
        allAppointments = appointments;
      }

      const mapped = allAppointments.map(mapAppointmentToEvent);

      try {
        let allSlotEvents: any[] = [];

        if (selectedBranchId === "all") {
          // Fetch slots for all branches
          for (const branch of branches) {
            try {
              const branchBizId = branch.business_id || branch.id;
              const slotResp = await getSlots({
                business_id: branchBizId,
                start_date: formatDateForPayload(visibleStart).replace(
                  /\//g,
                  "-"
                ),
                end_date: formatDateForPayload(visibleEnd).replace(/\//g, "-"),
              });
              let slotsData: any[] = [];
              if (!slotResp) slotsData = [];
              else if (slotResp.history && Array.isArray(slotResp.history))
                slotsData = slotResp.history;
              else if (slotResp.slots) slotsData = slotResp.slots;
              else if (slotResp.data && Array.isArray(slotResp.data))
                slotsData = slotResp.data;
              else if (Array.isArray(slotResp)) slotsData = slotResp;

              const slotEvents = buildSlotEvents(slotsData, branchBizId);
              allSlotEvents = [...allSlotEvents, ...slotEvents];
            } catch (error) {
              console.error(
                "Error fetching slots for branch:",
                branch.id,
                error
              );
            }
          }
        } else {
          // Fetch slots for the selected branch only
          const slotResp = await getSlots({
            business_id: businessId,
            start_date: formatDateForPayload(visibleStart).replace(/\//g, "-"),
            end_date: formatDateForPayload(visibleEnd).replace(/\//g, "-"),
          });
          let slotsData: any[] = [];
          if (!slotResp) slotsData = [];
          else if (slotResp.history && Array.isArray(slotResp.history))
            slotsData = slotResp.history;
          else if (slotResp.slots) slotsData = slotResp.slots;
          else if (slotResp.data && Array.isArray(slotResp.data))
            slotsData = slotResp.data;
          else if (Array.isArray(slotResp)) slotsData = slotResp;

          const slotEvents = buildSlotEvents(slotsData, businessId);
          allSlotEvents = slotEvents;
        }

        setEvents([...mapped, ...allSlotEvents]);
        setCalendarKey((prev) => prev + 1);
      } catch (e) {
        console.warn("Failed to fetch slots after appointment save", e);
        setEvents(mapped);
        setCalendarKey((prev) => prev + 1);
      }

      setAddDialogOpen(false);
      setEditingEventId(null);
      setFormClientId("");
      setFormServiceId("");
    } catch (err: any) {
      console.error("Error creating appointment", err);
      const errorMessage =
        err?.response?.data?.error ||
        err?.error ||
        (editingEventId
          ? "Failed to update appointment. Please try again."
          : "Failed to create appointment. Please try again.");
      toast.error(errorMessage);
    }
  };

  const handleDeleteAppointment = async () => {
    if (!editingEventId) return;
    if (
      !window.confirm("Are you sure you want to delete this appointment?")
    ) {
      return;
    }
    try {
      await deleteAppointment(editingEventId);
      await refreshAppointments();
      setCalendarKey((prev) => prev + 1);
      setAddDialogOpen(false);
      setEditingEventId(null);
      toast.success("Appointment deleted successfully!");
    } catch (error) {
      console.error("Error deleting appointment:", error);
      toast.error("Failed to delete appointment. Please try again.");
    }
  };

  const fetchInvoiceServices = async (businessId?: string | null) => {
    if (!businessId) {
      setInvoiceServices([]);
      return;
    }
    try {
      const response = await getAllServices(String(businessId));
      let servicesData = [];
      if (response && response.services) {
        servicesData = response.services;
      } else if (response && response.data && response.data.services) {
        servicesData = response.data.services;
      } else if (response && response.data && Array.isArray(response.data)) {
        servicesData = response.data;
      } else if (Array.isArray(response)) {
        servicesData = response;
      }
      setInvoiceServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (error) {
      console.error("Error fetching invoice services:", error);
      setInvoiceServices([]);
    }
  };

  const openInvoiceEditDialog = async () => {
    if (!editingEventId) {
      toast.error("No appointment selected.");
      return;
    }
    setInvoiceEditLoading(true);
    try {
      const response = await getInvoiceByAppointmentId(editingEventId);
      const invoiceData = response?.data ?? response;
      if (!invoiceData) {
        toast.error("No invoice found for this appointment.");
        return;
      }

      const businessId =
        invoiceData?.business_id ||
        invoiceData?.invoice?.business_id ||
        invoiceData?.businessId ||
        invoiceData?.invoice?.businessId;
      if (businessId) {
        await fetchInvoiceServices(String(businessId));
      } else if (formBranchId && formBranchId !== "all") {
        const branch = branches.find(
          (b) => String(b.id) === String(formBranchId)
        );
        const branchBusinessId = branch?.business_id || branch?.id;
        if (branchBusinessId) {
          await fetchInvoiceServices(String(branchBusinessId));
        }
      }

      setInvoiceEditingRow(invoiceData);
      setInvoiceEditOpen(true);
    } catch (error) {
      console.error("Error fetching invoice:", error);
      toast.error("Failed to load invoice.");
    } finally {
      setInvoiceEditLoading(false);
    }
  };

  const helperBranchId = clientFieldHelpersRef.current?.values?.branch_id;
  const normalizedHelperBranchId =
    helperBranchId && String(helperBranchId) !== "all"
      ? String(helperBranchId)
      : "";
  const addClientBranchId =
    normalizedHelperBranchId ||
    (formBranchId && formBranchId !== "all"
      ? String(formBranchId)
      : selectedBranchId !== "all"
      ? String(selectedBranchId)
      : venueId
      ? String(venueId)
      : null);

  const isReadOnlyAppointment = Boolean(
    editingEventId && editingPastAppointment
  );

  const appointmentFields: Field[] = (() => {
    const clientOptions =
      clients && Array.isArray(clients.users)
        ? clients.users.map((c) => ({
            label: formatClientLabel(c),
            value: String(c.id || c._id || ""),
          }))
        : [];
    const serviceOptions =
      services && Array.isArray(services)
        ? services.map((s) => {
            const svc: any = s as any;
            const name =
              svc?.name ||
              svc?.title ||
              svc?.service_name ||
              svc?.label ||
              `Service ${svc?.id || svc?._id || ""}`;
            const duration = svc?.duration ?? svc?.length ?? svc?.minutes;
            const price = svc?.price ?? svc?.cost;
            let label = String(name);
            if (typeof duration === "number" && !isNaN(duration)) {
              label += ` (${duration}m)`;
            }
            if (price !== undefined && price !== null && price !== "") {
              label += ` - ${price}`;
            }
            return {
              label,
              value: String(svc.id || svc._id || svc.service_id || ""),
            };
          })
        : [];
    const branchOptions = Array.isArray(branches)
      ? branches.map((branch: any) => ({
          label: String(
            branch?.name || branch?.title || branch?.id || "Branch"
          ),
          value: String(branch?.id || ""),
        }))
      : [];
    const loadBranchServices = async (branchId: string) => {
      try {
        const response = await getBranchServices(branchId);
        let servicesData: any[] = [];
        if (response && response.services) {
          servicesData = response.services;
        } else if (response && response.data && response.data.services) {
          servicesData = response.data.services;
        } else if (response && response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (Array.isArray(response)) {
          servicesData = response;
        }
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (error) {
        console.error("Error fetching branch services:", error);
      }
    };

    const branchField: Field = {
      name: "branch_id",
      label: "Branch",
      type: "dropdown" as const,
      colSpan: 2 as const,
      customRender: (value: any, _onChange: any, helpers: any) => {
        const currentValue = value ? String(value) : "";
        const hasCurrent = currentValue
          ? branchOptions.some(
              (option) => String(option.value) === currentValue
            )
          : false;
        const fallbackLabel =
          currentValue === "all"
            ? "All Branches"
            : currentValue
            ? `Branch ${currentValue}`
            : "Branch";
        const options =
          hasCurrent || !currentValue
            ? branchOptions
            : [
                { label: fallbackLabel, value: currentValue },
                ...branchOptions,
              ];
        return (
          <>
            <CustomLabel text="Branch" fontWeight={500} fontSize={14} />
            <CustomSelect
              label="Branch"
              value={currentValue}
              options={options}
              disabled={isReadOnlyAppointment}
              onChange={(v: any) => {
                const nextVal = String(v ?? "");
                if (helpers && helpers.setFieldValue) {
                  helpers.setFieldValue("branch_id", nextVal);
                  if (nextVal !== currentValue) {
                    helpers.setFieldValue("service_id", "");
                  }
                }
                if (nextVal && nextVal !== "all") {
                  void loadBranchServices(nextVal);
                }
              }}
              fullWidth
            />
          </>
        );
      },
    };
    const clientDisplayField: Field = {
      name: "client_summary",
      label: "Client",
      type: "text",
      colSpan: 2,
      customRender: () => {
        const raw = editingEventRaw || {};
        const rawClientId =
          raw?.client_id || raw?.clientId || raw?.client || formClientId;
        const matchedClient = (clients?.users || []).find(
          (c) => String(c.id || c._id || "") === String(rawClientId || "")
        );
        const displayName =
          pickNonEmptyString(
            matchedClient?.name,
            matchedClient?.email,
            resolveClientName(raw),
            formCustomerName
          ) || "Client";
        const displayPhone =
          pickNonEmptyString(
            matchedClient?.mobile,
            matchedClient?.phone,
            resolveClientPhone(raw)
          ) || "-";
        const canEditClient = Boolean(rawClientId);
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {canEditClient ? (
              <Button
                variant="text"
                onClick={() => openClientEditDialog(String(rawClientId || ""))}
                disabled={clientEditLoading || isReadOnlyAppointment}
                sx={{
                  p: 0,
                  minWidth: "unset",
                  width: "fit-content",
                  maxWidth: "100%",
                  display: "inline-flex",
                  alignSelf: "flex-start",
                  justifyContent: "flex-start",
                  textTransform: "none",
                  color: "text.primary",
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {displayName}
                </Typography>
              </Button>
            ) : (
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {displayName}
              </Typography>
            )}
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              {displayPhone}
            </Typography>
          </Box>
        );
      },
    };
    const clientField: Field = {
      name: "client_id",
      label: "Client",
      type: "dropdown",
      options: clientOptions,
      searchable: true,
      disabled: Boolean(editingEventId),
      customRender: (value, onChange, helpers, error) => {
        if (helpers) {
          clientFieldHelpersRef.current = helpers;
        }
        const resolvedBranchId =
          helpers?.values?.branch_id &&
          String(helpers.values.branch_id) !== "all"
            ? String(helpers.values.branch_id)
            : addClientBranchId;
        return (
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomLabel text="Client" fontWeight={500} fontSize={14} />
            <CustomSelect
              label="Client"
              value={value || ""}
              options={clientOptions}
              searchable={true}
              error={Boolean(error)}
              helperText={error}
              disabled={Boolean(editingEventId) || isReadOnlyAppointment}
              onChange={(v: any) => {
                const str = String(v ?? "");
                if (helpers && helpers.setFieldValue) {
                  helpers.setFieldValue("client_id", str);
                } else {
                    onChange(str);
                  }
                }}
                fullWidth
              />
            </Box>
            {!editingEventId && (
              <Button
                variant="contained"
                onClick={() => {
                  if (!resolvedBranchId) {
                    toast.warning(
                      "Please select a branch before adding a client"
                    );
                    return;
                  }
                  setAddClientDialogOpen(true);
                }}
                aria-label="Add client"
                disabled={isReadOnlyAppointment}
                sx={{
                  minWidth: 36,
                  width: 36,
                  height: 36,
                  minHeight: 36,
                  borderRadius: "10px",
                  p: 0,
                  lineHeight: 1,
                  mb: 0.2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "primary.main",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                <AddRounded sx={{ fontSize: 18 }} />
              </Button>
            )}
          </Box>
        );
      },
    };
    const serviceField: Field = {
      name: "service_id",
      label: "Service",
      type: "dropdown",
      required: Boolean(editingEventId),
      customRender: (value, onChange, helpers, error) => {
        const opts = serviceOptions.map((s) => ({
          label: s.label,
          value: s.value,
        }));
        return (
          <>
            <CustomLabel text="Service" fontWeight={500} fontSize={14} />

            <CustomSelect
              label="Service"
              value={value || ""}
              options={opts}
              error={Boolean(error)}
              helperText={error}
              disabled={isReadOnlyAppointment}
              onChange={(v: any) => {
                const str = String(v || "");
                if (helpers && helpers.setFieldValue) {
                  helpers.setFieldValue("service_id", str);
                  const currentStart =
                    helpers.values && helpers.values["time_start"]
                      ? helpers.values["time_start"]
                      : formTimeStart;
                  try {
                    const dur = findServiceDuration(str);
                    if (dur && currentStart) {
                      const newEndDesired = addMinutesToTime(
                        String(currentStart),
                        dur
                      );
                      const clamped = clampEndTime(
                        String(currentStart),
                        newEndDesired,
                        120
                      );
                      helpers.setFieldValue("time_end", clamped);
                    }
                  } catch (e) {}
                }
              }}
              fullWidth
            />
          </>
        );
      },
    };

    const fields: Field[] = [];
    if (editingEventId) {
      fields.push(clientDisplayField, branchField);
    } else {
      fields.push(clientField);
    }
    fields.push(serviceField);
    return [
      ...fields,
      {
        name: "date",
        label: "Date",
        type: "date",
        required: true,
        disabled: isReadOnlyAppointment,
      },
      {
        name: "time_start",
        label: "Start time",
        type: "text",
        required: true,
        disabled: false,
        customRender: (value, _onChange, helpers) => {
          const currentVal = value ? String(value) : "";
          const hasCurrent = currentVal
            ? timeOptions.some((option) => option.value === currentVal)
            : false;
          const startOptions =
            hasCurrent || !currentVal
              ? timeOptions
              : [{ label: currentVal, value: currentVal }, ...timeOptions];
          return (
            <>
              <CustomLabel text="Start time" fontWeight={500} fontSize={14} />
            <CustomSelect
              label="Start time"
              value={currentVal}
              options={startOptions}
              disabled={isReadOnlyAppointment}
              onChange={(v: any) => {
                const nextVal = String(v ?? "");
                if (helpers && helpers.setFieldValue) {
                  helpers.setFieldValue("time_start", nextVal);
                    const helperValues = helpers.values || {};
                    const serviceValue = Object.prototype.hasOwnProperty.call(
                      helperValues,
                      "service_id"
                    )
                      ? helperValues["service_id"]
                      : helperValues["service"];
                    const svcId =
                      serviceValue !== undefined && serviceValue !== null
                        ? String(serviceValue)
                        : formServiceId;
                    try {
                      const dur = findServiceDuration(String(svcId || ""));
                      if (dur && nextVal) {
                        const desired = addMinutesToTime(nextVal, dur);
                        const clamped = clampEndTime(nextVal, desired, 120);
                        helpers.setFieldValue("time_end", clamped);
                      }
                    } catch (e) {}
                  }
                }}
                fullWidth
              />
            </>
          );
        },
      },
      {
        name: "time_end",
        label: "End time",
        type: "text",
        required: true,
        disabled: true,
      },
      {
        name: "notes",
        label: "Notes",
        type: "text",
        disabled: isReadOnlyAppointment,
      },
      ...(editingEventId
        ? [
            {
              name: "status_and_delete",
              label: "Actions",
              type: "text" as const,
              colSpan: 2 as const,
              customRender: (value: any, onChange: any, helpers: any) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <CustomLabel text="Status" fontWeight={500} fontSize={14} />
                    <CustomSelect
                      label="Status"
                      value={helpers?.values?.status || "Pending"}
                      options={[
                        { label: "Cancelled", value: "Cancelled" },
                        { label: "Pending", value: "Pending" },
                        { label: "Done", value: "Done" },
                      ]}
                      disabled={isReadOnlyAppointment}
                      onChange={(v: any) => {
                        if (helpers && helpers.setFieldValue) {
                          helpers.setFieldValue("status", v);
                          setFormStatus(v);
                        }
                        }}
                        fullWidth
                      />
                    </Box>
                  </Box>
                );
              },
            },
          ]
        : []),
    ];
  })();

  const blockFields: Field[] = [
    {
      name: "block_action",
      label: "Action",
      type: "dropdown",
      options: [
        { label: "Open Slot", value: "open" },
        { label: "Close Slot", value: "close" },
      ],
      required: true,
      colSpan: 2,
    },
    {
      name: "block_date",
      label: "Date",
      type: "date",
      required: true,
      colSpan: 2,
    },
    {
      name: "block_start",
      label: "Start time",
      type: "dropdown",
      options: timeOptions,
      required: true,
      colSpan: 2,
    },
    {
      name: "block_end",
      label: "End time",
      type: "dropdown",
      options: timeOptions,
      required: true,
      colSpan: 2,
    },
    {
      name: "block_branch_info",
      label: "Branch",
      type: "text",
      colSpan: 2,
      customRender: () => {
        const bId = formBranchId || selectedBranchId;
        const branch = branches.find((b) => String(b.id) === String(bId));
        return (
          <Box
            sx={{
              padding: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              fontSize: "0.9rem",
            }}
          >
            <strong>Selected Branch:</strong> {branch?.name || "All"}
          </Box>
        );
      },
    },
  ];

  const dialogTabs = editingEventId
    ? undefined
    : [
        { label: "Add Appointment", fields: appointmentFields },
        { label: "Block Slots", fields: blockFields },
      ];

  const dialogSubmitLabel = editingEventId
    ? "Update"
    : appointmentDialogTab === 1
    ? "Apply"
    : "Create";
  const dialogShowSubmit = !isReadOnlyAppointment;
  const handleAppointmentDialogClose = () => {
    setAddDialogOpen(false);
    setEditingEventId(null);
    setEditingEventRaw(null);
    setEditingPastAppointment(false);
  };
  const handleInvoiceDialogClose = () => {
    setInvoiceEditOpen(false);
    setInvoiceEditingRow(null);
    setInvoiceServices([]);
  };
  const dialogTitleText = editingEventId ? "Edit Appointment" : "Add Appointment";
  const dialogTitle = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
          {dialogTitleText}
        </Typography>
        <IconButton aria-label="Close" onClick={handleAppointmentDialogClose}>
          <CloseRounded />
        </IconButton>
      </Box>
      <Divider />
    </Box>
  );
  const appointmentDialogActions = editingEventId
    ? [
        <CustomButton
          key="invoice"
          label={invoiceEditLoading ? "Loading..." : "Invoice"}
          variant="outlined"
          onClick={openInvoiceEditDialog}
          disabled={invoiceEditLoading}
          borderColor={COLORS.MainColor}
          textColor={COLORS.MainColor}
          bgColor={COLORS.SidebarActiveBg}
          hoverBgColor="#d4ecff"
        />,
        <CustomButton
          key="delete"
          label="Delete Appointment"
          bgColor={COLORS.RED}
          textColor={COLORS.WhiteText}
          onClick={handleDeleteAppointment}
          disabled={isReadOnlyAppointment}
        />,
      ]
    : [];

  const dialogInitialValues = React.useMemo(
    () => ({
      client_id: formClientId || "",
      service_id: formServiceId || "",
      branch_id: formBranchId || selectedBranchId || "all",
      date: formDate,
      time_start: formTimeStart,
      time_end: formTimeEnd,
      notes: formNotes,
      status: formStatus || "Pending",
      block_action: blockAction,
      block_date: blockDate,
      block_start: blockStart,
      block_end: blockEnd,
    }),
    [
      formClientId,
      formServiceId,
      formBranchId,
      selectedBranchId,
      formDate,
      formTimeStart,
      formTimeEnd,
      formNotes,
      formStatus,
      blockAction,
      blockDate,
      blockStart,
      blockEnd,
    ]
  );

  const clientEditFields: Field[] = React.useMemo(
    () => [
      { name: "fullName", label: "Full Name", required: true },
      { name: "email", label: "Email" },
      {
        name: "countryCode",
        label: "Country Code",
        type: "dropdown",
        options: countries.map((c) => ({
          label: `+${c.phone} - ${c.label}`,
          value: `+${c.phone}`,
        })),
        searchable: true,
      },
      { name: "phoneNumber", label: "Phone Number" },
      {
        name: "gender",
        label: "Gender",
        type: "dropdown",
        options: [
          { label: "Male", value: "male" },
          { label: "Female", value: "female" },
        ],
      },
      { name: "dob", label: "Date of Birth", type: "date" },
    ],
    []
  );

  const clientEditInitialValues = React.useMemo(() => {
    if (!editingClient) return undefined;
    return {
      fullName: editingClient.name || "",
      email: editingClient.email || "",
      countryCode: normalizeClientCountryCode(editingClient.country_code || ""),
      phoneNumber: editingClient.mobile || editingClient.phone || "",
      gender: normalizeClientGenderValue(editingClient.gender || ""),
      dob: formatClientDateOnly(editingClient.dob || ""),
    };
  }, [editingClient]);

  const invoiceFormFields: Field[] = React.useMemo(() => {
    const clientOptions =
      clients && Array.isArray(clients.users)
        ? clients.users.map((c) => ({
            label: formatClientLabel(c),
            value: String(c.id || c._id || ""),
          }))
        : [];

    return [
      {
        name: "clientName",
        label: "Client",
        type: "dropdown",
        options: clientOptions,
        searchable: true,
        customRender: (value, onChange, helpers, error) => {
          return (
            <>
              <CustomLabel text="Client" fontWeight={500} fontSize={14} />
              <CustomSelect
                label="Client"
                value={value || ""}
                options={clientOptions}
                searchable={true}
                error={Boolean(error)}
                helperText={error}
                onChange={(v: any) => {
                  const str = String(v ?? "");
                  if (helpers && helpers.setFieldValue) {
                    helpers.setFieldValue("clientName", str);
                  } else {
                    onChange(str);
                  }
                  if (!str) return;
                  const selected = (clients?.users || []).find(
                    (c) => String(c.id || c._id || "") === str
                  );
                  if (selected) {
                    helpers?.setFieldValue("email", selected.email || "");
                    helpers?.setFieldValue(
                      "phoneNumber",
                      selected.mobile || selected.phone || ""
                    );
                    return;
                  }
                  getClientById(str)
                    .then((client) => {
                      if (client && client.user) {
                        helpers?.setFieldValue(
                          "email",
                          client.user.email || ""
                        );
                        helpers?.setFieldValue(
                          "phoneNumber",
                          client.user.mobile || ""
                        );
                      }
                    })
                    .catch(console.error);
                }}
                fullWidth
                placeholder="Select..."
              />
            </>
          );
        },
      },
      { name: "email", label: "Email" },
      { name: "phoneNumber", label: "Phone Number" },
      { name: "invoice_date", label: "Date", type: "date" },
      {
        name: "status",
        label: "Status",
        type: "dropdown",
        options: ["paid", "cancelled", "unpaid"],
      },
      {
        name: "paymentMethod",
        label: "Payment method",
        type: "dropdown",
        options: ["cash", "card", "bank transfer"],
      },
      { name: "notes", label: "Notes", colSpan: 2 },
      {
        name: "items",
        label: "Items",
        colSpan: 2,
        customRender: (_value: any, onChange: (v: any) => void, helpers) => (
          <InvoiceItemsEditor
            value={_value}
            onChange={onChange}
            vatValue={helpers?.values?.vat || 0}
            onVatChange={(v) => helpers?.setFieldValue("vat", v)}
            discountValue={helpers?.values?.discount || 0}
            onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
            isEditMode={Boolean(invoiceEditingRow)}
            servicesList={invoiceServices}
          />
        ),
      },
    ];
  }, [clients, invoiceEditingRow, invoiceServices]);

  const invoiceDialogInitialValues = React.useMemo(() => {
    if (!invoiceEditingRow) {
      return { vat: "0", discount: "0", paymentMethod: "cash" };
    }

    const clientId =
      invoiceEditingRow.invoice?.client_id || invoiceEditingRow.client_id || "";
    const matchedClient = (clients?.users || []).find(
      (c) => String(c.id || c._id || "") === String(clientId || "")
    );
    const invoiceDate =
      invoiceEditingRow.invoice?.created_at ||
      invoiceEditingRow.created_at ||
      invoiceEditingRow.invoice?.invoice_date ||
      invoiceEditingRow.invoice_date;
    const details = resolveInvoiceDetails(invoiceEditingRow);
    const items = buildInvoiceItems(details, invoiceServices);

    return {
      clientName: String(clientId || ""),
      email:
        invoiceEditingRow.client?.email ||
        matchedClient?.email ||
        "",
      phoneNumber:
        invoiceEditingRow.client?.mobile ||
        matchedClient?.mobile ||
        matchedClient?.phone ||
        "",
      invoice_date: formatInvoiceDateValue(invoiceDate),
      status: resolveInvoiceStatusFormValue(
        pickNonEmptyString(
          invoiceEditingRow.status,
          invoiceEditingRow.invoice?.status,
          invoiceEditingRow.invoice?.status_name,
          invoiceEditingRow.invoice?.status_label
        ),
        invoiceEditingRow.invoice?.status_id ??
          invoiceEditingRow.status_id ??
          invoiceEditingRow.invoice?.status?.id ??
          invoiceEditingRow.status?.id
      ),
      paymentMethod: resolveInvoicePaymentMethodValue(
        invoiceEditingRow.invoice?.payment_method_id ||
          invoiceEditingRow.payment_method_id
      ),
      notes: invoiceEditingRow.invoice?.notes || invoiceEditingRow.notes || "",
      items,
      invoice_number:
        invoiceEditingRow.invoice?.Invoice_number ||
        invoiceEditingRow.invoice?.invoice_number ||
        invoiceEditingRow.Invoice_number ||
        invoiceEditingRow.invoice_number,
      appointment_id:
        invoiceEditingRow.invoice?.appointment_id ||
        invoiceEditingRow.appointment_id ||
        editingEventId ||
        "1",
      net_amount:
        invoiceEditingRow.invoice?.net_amount || invoiceEditingRow.net_amount,
      total_amount:
        invoiceEditingRow.invoice?.total_amount || invoiceEditingRow.total_amount,
      vat: String(invoiceEditingRow.invoice?.Vat || invoiceEditingRow.Vat || 0),
      discount: String(
        invoiceEditingRow.invoice?.discount || invoiceEditingRow.discount || 0
      ),
    };
  }, [invoiceEditingRow, invoiceServices, clients, editingEventId]);

  const handleInvoiceSubmit = async (values: Record<string, any>) => {
    if (!invoiceEditingRow) {
      toast.error("No invoice data available.");
      return;
    }
    const invoiceId = invoiceEditingRow.invoice?.id || invoiceEditingRow.id;
    if (!invoiceId) {
      toast.error("Invoice ID not found.");
      return;
    }

    try {
      const servicesPayload = (
        Array.isArray(values.items) ? values.items : []
      ).map((item: any) => ({
        service_id: item.serviceId || String(item.service),
        quantity: item.quantity,
        amount: item.unitCost,
        discount: item.discount,
        net_amount: item.totalPrice,
      }));

      const totalAmount = servicesPayload.reduce(
        (sum: number, item: any) => sum + Number(item.net_amount || 0),
        0
      );
      const discountPercent = Number(values.discount) || 0;
      const discountAmount =
        Math.round(((totalAmount * discountPercent) / 100) * 100) / 100;
      const amountAfterDiscount = Math.max(0, totalAmount - discountAmount);
      const vatPercent = Number(values.vat) || 0;
      const vatAmount =
        Math.round(((amountAfterDiscount * vatPercent) / 100) * 100) / 100;
      const netAmount =
        Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

      let businessIdToUse =
        invoiceEditingRow.business_id ||
        invoiceEditingRow.invoice?.business_id ||
        invoiceEditingRow.businessId ||
        invoiceEditingRow.invoice?.businessId;
      if (!businessIdToUse) {
        const branchId =
          formBranchId && formBranchId !== "all"
            ? formBranchId
            : selectedBranchId && selectedBranchId !== "all"
            ? selectedBranchId
            : null;
        if (branchId) {
          const branch = branches.find(
            (b) => String(b.id) === String(branchId)
          );
          businessIdToUse = branch?.business_id || branch?.id;
        }
      }

      const payload = {
        client_id: values.clientName,
        appointment_id: values.appointment_id || editingEventId || "1",
        business_id: businessIdToUse,
        status_id:
          values.status === "paid"
            ? 1
            : values.status === "unpaid"
            ? 2
            : 3,
        invoice_number:
          values.invoice_number ||
          invoiceEditingRow.invoice?.Invoice_number ||
          invoiceEditingRow.invoice?.invoice_number ||
          invoiceEditingRow.Invoice_number ||
          invoiceEditingRow.invoice_number,
        total_amount: totalAmount,
        discount: discountPercent,
        payment_method_id:
          values.paymentMethod === "card"
            ? 1
            : values.paymentMethod === "cash"
            ? 2
            : 3,
        created_by: getUserId() || "1",
        created_at: new Date().toISOString(),
        Vat: vatPercent,
        net_amount: String(netAmount),
        services: servicesPayload,
        notes: values.notes,
        invoice_date: values.invoice_date,
        id: String(invoiceId),
      };

      await updateInvoice(String(invoiceId), payload);
      toast.success("Invoice updated successfully!");
      handleInvoiceDialogClose();
    } catch (error) {
      console.error("Error saving invoice:", error);
      toast.error("Failed to update invoice.");
    }
  };

  const courts: Court[] = [
    { id: "all", name: "All Courts", type: "filter", color: "#2196F3" },
    ...((Array.isArray(branches) ? branches : []).map((b: any) => ({
      id: String(b.id),
      name: String(b.name || b.title || b.id || "Court"),
      type: "branch",
      color: "#4CAF50",
    })) as Court[]),
  ];

  return (
    <div className="appointment-page">
      <Dialog
        open={Boolean(dragConfirmState)}
        onClose={() => {
          if (dragConfirmState) {
            dragConfirmState.resolve(false);
            setDragConfirmState(null);
          }
        }}
        sx={{
          // Force this confirmation dialog above all calendar content
          zIndex: 99999,
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            zIndex: 100000,
          },
        }}
        BackdropProps={{
          sx: {
            zIndex: 99998,
          },
        }}
        TransitionProps={{ appear: false, timeout: 0 }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Confirm Move
        </DialogTitle>
        <DialogContent sx={{ minWidth: 320, pt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You are about to move this appointment:
          </Typography>
          <Typography variant="body2">
            <strong>From branch:</strong> {dragConfirmState?.fromBranch || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>To branch:</strong> {dragConfirmState?.toBranch || "-"}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Current time
            </Typography>
            <Typography variant="body2">
              Start: {dragConfirmState?.fromStart || "-"}
            </Typography>
            <Typography variant="body2">
              End: {dragConfirmState?.fromEnd || "-"}
            </Typography>
          </Box>
          <Box sx={{ mt: 1.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              New time
            </Typography>
            <Typography variant="body2">
              Start: {formatTo12h(dragConfirmState?.toStart)}
            </Typography>
            <Typography variant="body2">
              End: {formatTo12h(dragConfirmState?.toEnd)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (dragConfirmState) {
                dragConfirmState.resolve(false);
                setDragConfirmState(null);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (dragConfirmState) {
                dragConfirmState.resolve(true);
                setDragConfirmState(null);
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <div className="appointment-header-section">
        <Box
          sx={{
            display: { xs: "flex", lg: "grid" },
            gridTemplateColumns: {
              lg: "minmax(0, 1fr) auto minmax(0, 1fr)",
            },
            columnGap: { lg: 2 },
            rowGap: 1,
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center", lg: "center" },
            justifyContent: { xs: "flex-start", md: "space-between" },
            gap: { xs: 1, sm: 1 },
            width: "100%",
            flexWrap: { xs: "wrap", md: "wrap" },
            paddingY: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: 1,
              flexWrap: { xs: "wrap", md: "wrap" },
              flex: { xs: "0 0 auto", md: 1 },
              width: { xs: "100%", md: "auto", lg: "auto" },
              minWidth: 0,
              justifySelf: { lg: "start" },
            }}
          >
            <CustomSelect
              value={selectedBranchId}
              onChange={(value) => handleBranchChange(String(value))}
              options={[
                { value: "all", label: "All Branches" },
                ...branches.map((branch) => ({
                  value: String(branch.id),
                  label: String(
                    branch?.name || branch?.title || branch?.id || "Branch"
                  ),
                })),
              ]}
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                maxWidth: { xs: "100%", sm: 240 },
                width: { xs: "100%", sm: 200 },
                flexShrink: 0,
                "& .MuiOutlinedInput-root": {
                  height: 32,
                  minHeight: 32,
                  borderRadius: "10px",
                },
                "& .MuiSelect-select": {
                  padding: "6px 10px",
                  fontSize: "0.85rem",
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: { xs: 0.5, md: 1 },
                flexWrap: { xs: "wrap", md: "nowrap" },
                alignItems: { xs: "stretch", md: "center" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <CustomButton
                variant="contained"
                onClick={async () => {
                  try {
                    await exportSlotsToWhatsApp();
                  } catch (e) {
                    console.error("Export slots failed", e);
                    alert("Failed to export slots");
                  }
                }}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  backgroundColor: "green",
                  height: { xs: "auto", sm: 32 },
                  minHeight: { xs: 28, sm: 32 },
                  px: { xs: 0.75, sm: 1.25 },
                  fontSize: { xs: "0.7rem", sm: "0.78rem" },
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  lineHeight: 1.1,
                  minWidth: 0,
                  flex: { xs: "1 1 0", sm: "0 0 auto" },
                  textAlign: "center",
                }}
              >
                Export Available Slots
              </CustomButton>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              flex: { xs: "0 0 auto", md: 1 },
              flexWrap: "nowrap",
              minWidth: { xs: "100%", md: "auto" },
              width: { xs: "100%", md: "auto", lg: "fit-content" },
              justifySelf: { lg: "center" },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={gotoPrev}
              sx={{
                minWidth: { xs: 28, sm: 32 },
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                px: 0,
              }}
            >
              <ChevronLeftRounded fontSize="small" />
            </Button>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={activeDate}
                onChange={(date) => {
                  if (!date || isNaN(date.getTime())) return;
                  gotoDate(date);
                }}
                format="yyyy-MM-dd"
                slotProps={{
                  textField: {
                    size: "small",
                    hiddenLabel: true,
                    inputProps: {
                      "aria-label": "Date",
                      style: { textAlign: "center" },
                    },
                    sx: {
                      minWidth: { xs: 0, md: 190 },
                      width: { xs: "100%", md: "auto" },
                      flex: { xs: 1, md: "0 0 auto" },
                      "& .MuiInputBase-root": {
                        height: 32,
                        minHeight: 32,
                        borderRadius: "10px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 10px",
                        fontSize: "0.85rem",
                        textAlign: "center",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              size="small"
              onClick={gotoNext}
              sx={{
                minWidth: { xs: 28, sm: 32 },
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                px: 0,
              }}
            >
              <ChevronRightRounded fontSize="small" />
            </Button>
          </Box>

          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              gap: 0.75,
              justifyContent: "flex-start",
              flexWrap: "wrap",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              variant={currentView === "week" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 30,
                minHeight: 30,
                px: 1,
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("week")}
            >
              Weekly
            </Button>
            <Button
              variant={currentView === "day" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 30,
                minHeight: 30,
                px: 1,
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("day")}
            >
              Daily
            </Button>
            <Button
              variant={currentView === "month" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 30,
                minHeight: 30,
                px: 1,
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("month")}
            >
              Monthly
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={gotoToday}
              sx={{
                height: 30,
                minHeight: 30,
                px: 1,
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
              }}
            >
              
            </Button>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 1,
              justifyContent: "flex-end",
              flexWrap: "wrap",
              alignItems: "center",
              minWidth: { xs: "100%", md: "auto", lg: 0 },
              minHeight: 32,
              justifySelf: { lg: "end" },
            }}
          >
            <Button
              variant={currentView === "week" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 32,
                minHeight: 32,
                px: 1.25,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("week")}
            >
              Weekly
            </Button>
            <Button
              variant={currentView === "day" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 32,
                minHeight: 32,
                px: 1.25,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("day")}
            >
              Daily
            </Button>
            <Button
              variant={currentView === "month" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 32,
                minHeight: 32,
                px: 1.25,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("month")}
            >
              Monthly
            </Button>
            {/* <Button
              variant="text"
              size="small"
              onClick={gotoToday}
              sx={{
                height: 32,
                minHeight: 32,
                px: 1.25,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
              }}
            >
              Today
            </Button> */}
          </Box>
        </Box>

        <AddClientDialog
          open={addClientDialogOpen}
          onClose={() => setAddClientDialogOpen(false)}
          branches={branches}
          initialBranch={addClientBranchId}
          onCreated={(client) => {
            try {
              if (!client) return;
              const normalizedClient = normalizeClientRecord(client);
              const newClientId = normalizedClient.id
                ? String(normalizedClient.id)
                : "";
              setClients((prev) => {
                const existing =
                  prev && Array.isArray(prev.users) ? prev.users : [];
                const deduped = existing.filter(
                  (c) => String(c.id) !== String(normalizedClient.id)
                );
                return { users: [normalizedClient, ...deduped] };
              });

              if (newClientId && clientFieldHelpersRef.current?.setFieldValue) {
                clientFieldHelpersRef.current.setFieldValue(
                  "client_id",
                  newClientId
                );
              } else if (newClientId && !addDialogOpen) {
                setFormClientId(newClientId);
              }
              const name = normalizedClient.name || normalizedClient.email;
              if (name) setFormCustomerName(String(name));
              void fetchOrgClients(normalizedClient);
            } catch (e) {
              console.error("Error while adding client to appointment: ", e);
            }
          }}
        />
      </div>

      <Box
        className="scheduler-wrapper"
        ref={schedulerWrapperRef}
        sx={{
          marginTop: 1,
        }}
      >
        {slotDurationLoading ? (
          <Box
            sx={{
              height: "100%",
              minHeight: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
              fontWeight: 500,
            }}
          >
            Loading calendar...
          </Box>
        ) : (
          <GridCalendar
            events={events}
            branches={displayedBranches}
            activeDate={activeDate}
            view={currentView}
            disableDrag={isMobileInteraction}
            displayedBranchIds={displayedBranchIds}
            onDateClick={(dateObj, branchId) => {
              handleDateClick(
                {
                  date: dateObj,
                  dayEl: null,
                  dateStr: dateObj.toISOString().split("T")[0],
                },
                branchId
              );
            }}
            onEventClick={(eventId) => {
              const event = events.find((e) => String(e.id) === String(eventId));
              if (event) {
                handleEventClick({
                  event: {
                    id: eventId,
                    start: event.start,
                    end: event.end,
                    title: event.title,
                    extendedProps: event.extendedProps,
                  },
                });
              }
            }}
            onEventDoubleClick={(eventId) => {
              const event = events.find((e) => String(e.id) === String(eventId));
              if (event) {
                handleEventClick({
                  event: {
                    id: eventId,
                    start: event.start,
                    end: event.end,
                    title: event.title,
                    extendedProps: event.extendedProps,
                  },
                });
              }
            }}
            onDragValidationFailed={(message) => {
              toast.error(message);
            }}
            onEventDragEnd={async (eventId, newStartTime, newEndTime, branchId) => {
              const event = events.find((e) => String(e.id) === String(eventId));
              if (!event) return;
              if (isClosedSlotEvent(event)) {
                toast.error("Closed by admin.");
                return;
              }

              const raw = event.extendedProps?.raw || {};
              const user = JSON.parse(localStorage.getItem("user") || "{}");
              const targetBranch = branches.find(
                (branch) => String(branch.id) === String(branchId)
              );
              const originalBranchId =
                event.extendedProps?.branchId ||
                raw.branch_id ||
                raw.branchId ||
                raw.business_id ||
                raw.businessId;
              const targetBusinessId =
                (targetBranch && (targetBranch.business_id || targetBranch.id)) ||
                raw.business_id ||
                raw.businessId ||
                (branches &&
                  branches[0] &&
                  (branches[0].business_id || branches[0].id));

              // Get service ID from the appointment - try multiple paths
              let serviceId = "";
              if (raw.services && Array.isArray(raw.services) && raw.services.length > 0) {
                const firstService = raw.services[0];
                serviceId = typeof firstService === "object" 
                  ? (firstService.id || firstService._id || firstService.service_id || firstService)
                  : firstService;
              } else if (raw.service_id) {
                serviceId = raw.service_id;
              } else if (raw.service) {
                serviceId = typeof raw.service === "object" 
                  ? (raw.service.id || raw.service._id || raw.service)
                  : raw.service;
              }

              // Calculate end time based on service duration using multiple strategies
              let calculatedEndTime = newEndTime;
              let durationMinutes = null;

              // Strategy 1: Try to find service duration from services array by ID (with API fallback if needed)
              if (serviceId) {
                durationMinutes = await findServiceDurationAsync(String(serviceId), branchId);
                console.log("Drag Strategy 1: Service ID lookup:", serviceId, "Duration:", durationMinutes);
              }

              // Strategy 2: Try to extract duration from appointment data
              if (!durationMinutes) {
                durationMinutes = getAppointmentDuration(raw);
                console.log("Drag Strategy 2: Duration from appointment data:", durationMinutes);
              }

              // Strategy 3: Fallback to calculating from original times
              if (!durationMinutes) {
                const originalStart =
                  event.extendedProps?.timeStart ||
                  raw.time_start ||
                  raw.start_time ||
                  raw.timeStart ||
                  raw.startTime ||
                  raw.time ||
                  "";
                const originalEnd =
                  event.extendedProps?.timeEnd ||
                  raw.time_end ||
                  raw.end_time ||
                  raw.timeEnd ||
                  raw.endTime ||
                  raw.end ||
                  "";
                
                if (
                  originalStart &&
                  originalEnd &&
                  originalStart.includes(":") &&
                  originalEnd.includes(":")
                ) {
                  try {
                    const [origHours, origMins] = originalStart
                      .split(":")
                      .map(Number);
                    const [origEndHours, origEndMins] = originalEnd
                      .split(":")
                      .map(Number);
                    
                    if (
                      !isNaN(origHours) &&
                      !isNaN(origMins) &&
                      !isNaN(origEndHours) &&
                      !isNaN(origEndMins)
                    ) {
                      const origStartTotal = origHours * 60 + origMins;
                      const origEndTotal = origEndHours * 60 + origEndMins;
                      durationMinutes =
                        origEndTotal > origStartTotal
                          ? origEndTotal - origStartTotal
                          : (origEndTotal + 1440) - origStartTotal;
                      
                      console.log(
                        "Drag Strategy 3: Duration from original times:",
                        durationMinutes
                      );
                    }
                  } catch (e) {
                    console.warn("Drag: Failed to parse original times:", e);
                  }
                }
              }

              // Strategy 4: Fallback to event start/end timestamps
              if (!durationMinutes) {
                try {
                  const eventStart =
                    event.start instanceof Date
                      ? event.start
                      : new Date(event.start);
                  const eventEnd =
                    event.end instanceof Date ? event.end : new Date(event.end);
                  if (
                    !isNaN(eventStart.getTime()) &&
                    !isNaN(eventEnd.getTime())
                  ) {
                    const diffMinutes = Math.round(
                      (eventEnd.getTime() - eventStart.getTime()) / 60000
                    );
                    if (diffMinutes > 0) {
                      durationMinutes = diffMinutes;
                      console.log(
                        "Drag Strategy 4: Duration from event timestamps:",
                        durationMinutes
                      );
                    }
                  }
                } catch (e) {
                  console.warn("Drag: Failed to read event timestamps:", e);
                }
              }

              // Apply the calculated duration to the new start time
              if (durationMinutes && durationMinutes > 0) {
                try {
                  const [newHours, newMins] = newStartTime.split(':').map(Number);
                  
                  if (!isNaN(newHours) && !isNaN(newMins)) {
                    const newStartTotal = newHours * 60 + newMins;
                    const newEndTotal = newStartTotal + durationMinutes;
                    
                    // IMPORTANT: Validate BEFORE capping - if end time would exceed midnight, reject the drag
                    const MAX_END_TIME = 24 * 60; // 1440 minutes = 24:00 / midnight
                    if (newEndTotal > MAX_END_TIME) {
                      console.error("Drag: End time exceeds midnight. Start:", newStartTime, "Duration:", durationMinutes, "End would be:", newEndTotal);
                      
                      // Calculate what the end time would have been
                      const wouldBeHours = Math.floor(newEndTotal / 60);
                      const wouldBeMins = newEndTotal % 60;
                      const wouldBeTime = `${String(wouldBeHours).padStart(2, '0')}:${String(wouldBeMins).padStart(2, '0')}`;
                      
                      // Show error toast and return WITHOUT calling API
                      toast.error(`Cannot schedule appointment. Start time ${newStartTime} with ${durationMinutes}-minute service would end at ${wouldBeTime} (after midnight). Please drag earlier or choose a shorter service.`);
                      
                      // Force calendar re-render to reset appointment to original position
                      setCalendarKey(prev => prev + 1);
                      return;
                    }
                    
                    // Now it's safe to cap the end time (though it shouldn't need capping after the validation above)
                    const finalEndTotal = Math.min(newEndTotal, MAX_END_TIME);
                    
                    const newEndHours = Math.floor(finalEndTotal / 60);
                    const newEndMins = finalEndTotal % 60;
                    calculatedEndTime = `${String(newEndHours).padStart(2, '0')}:${String(newEndMins).padStart(2, '0')}`;
                    
                    console.log("Drag: Final calculated end time:", calculatedEndTime, "Duration:", durationMinutes, "Validation passed");
                  }
                } catch (e) {
                  console.error("Drag: Failed to calculate end time:", e);
                }
              } else {
                console.warn("Drag: Could not determine duration, using provided end time:", newEndTime);
              }

              // If nothing changed (branch + times), skip confirmation and update
              const originalStart =
                event.extendedProps?.timeStart ||
                raw.time_start ||
                raw.start_time ||
                newStartTime;
              const originalEnd =
                event.extendedProps?.timeEnd ||
                raw.time_end ||
                raw.end_time ||
                calculatedEndTime;
              const isSameBranch =
                String(originalBranchId || "") === String(branchId || "");
              const isSameTimes =
                String(originalStart || "") === String(newStartTime || "") &&
                String(originalEnd || "") === String(calculatedEndTime || "");
              if (isSameBranch && isSameTimes) {
                return;
              }

              // Ask for confirmation BEFORE updating
              const confirmMove = await requestDragConfirmation({
                fromBranch:
                  branches.find((b) => String(b.id) === String(originalBranchId))
                    ?.name || String(originalBranchId || "Current"),
                toBranch:
                  targetBranch?.name ||
                  targetBranch?.title ||
                  String(branchId || "Target"),
                fromStart:
                  event.extendedProps?.timeStart ||
                  raw.time_start ||
                  raw.start_time ||
                  newStartTime,
                fromEnd:
                  event.extendedProps?.timeEnd ||
                  raw.time_end ||
                  raw.end_time ||
                  calculatedEndTime,
                toStart: newStartTime,
                toEnd: calculatedEndTime,
              });
              if (!confirmMove) {
                throw new Error("Drag cancelled by user");
              }

              const isBranchChange =
                String(originalBranchId || "") !== String(branchId || "");
              
              // Store original event for potential restoration on error
              const originalEvent = event;
              let eventWasRemoved = false;
              
              if (isBranchChange) {
                setEvents((prev) =>
                  (Array.isArray(prev) ? prev : []).filter(
                    (e) => String(e.id) !== String(eventId)
                  )
                );
                eventWasRemoved = true;
              }

              const payload: any = {
                all_day: 0,
                business_id: targetBusinessId,
                client_id: raw.client_id || raw.client || null,
                services: raw.services && Array.isArray(raw.services) ? raw.services : raw.service ? [raw.service] : raw.service_id ? [raw.service_id] : [],
                flag: raw.flag || "normal",
                notes: raw.notes || raw.text || "",
                number: raw.number || 1,
                servicePrice: raw.servicePrice || raw.price || "0",
                start_date: formatDateForPayload(activeDate),
                status: raw.status || raw.state || "Pending",
                time_end: calculatedEndTime,
                time_start: newStartTime,
                user_id: raw.user_id || user.id || user._id,
              };

              try {
                await updateAppointment(eventId.toString(), payload);
                
                // Refresh appointments based on view
                let allAppointments: any[] = [];
                
                if (selectedBranchId === "all") {
                  for (const branch of branches) {
                    try {
                      const branchBizId = branch.business_id || branch.id;
                      const resp = await getAllAppointments(branchBizId, {
                        viewType: currentView,
                      });
                      let appointments: any[] = [];
                      if (!resp) {
                        appointments = [];
                      } else if (resp.today && resp.all) {
                        const allData = resp.all;
                        if (allData.appointments) appointments = allData.appointments;
                        else if (allData.data) appointments = allData.data;
                        else if (Array.isArray(allData)) appointments = allData;
                      } else if (resp.appointments) {
                        appointments = resp.appointments;
                      } else if (resp.data) {
                        appointments = resp.data;
                      } else if (Array.isArray(resp)) {
                        appointments = resp;
                      }
                      allAppointments = [...allAppointments, ...appointments];
                    } catch (error) {
                      console.error("Error fetching appointments for branch:", branch.id, error);
                    }
                  }
                } else {
                  const branchBizId = targetBusinessId;
                  const resp = await getAllAppointments(branchBizId, {
                    viewType: currentView,
                  });
                  let appointments: any[] = [];
                  if (!resp) {
                    appointments = [];
                  } else if (resp.today && resp.all) {
                    const allData = resp.all;
                    if (allData.appointments) appointments = allData.appointments;
                    else if (allData.data) appointments = allData.data;
                    else if (Array.isArray(allData)) appointments = allData;
                  } else if (resp.appointments) {
                    appointments = resp.appointments;
                  } else if (resp.data) {
                    appointments = resp.data;
                  } else if (Array.isArray(resp)) {
                    appointments = resp;
                  }
                  allAppointments = appointments;
                }

                const mapped = allAppointments.map(mapAppointmentToEvent);

                try {
                  const startDateParam = formatDateForPayload(visibleStart).replace(
                    /\//g,
                    "-"
                  );
                  const endDateParam = formatDateForPayload(visibleEnd).replace(
                    /\//g,
                    "-"
                  );
                  let slotEvents: any[] = [];

                  if (selectedBranchId === "all") {
                    for (const branch of branches) {
                      try {
                        const branchBizId = branch?.business_id || branch?.id;
                        if (!branchBizId) continue;
                        const slotResp = await getSlots({
                          business_id: branchBizId,
                          start_date: startDateParam,
                          end_date: endDateParam,
                        });
                        let slotsData: any[] = [];
                        if (!slotResp) slotsData = [];
                        else if (slotResp.history && Array.isArray(slotResp.history))
                          slotsData = slotResp.history;
                        else if (slotResp.slots) slotsData = slotResp.slots;
                        else if (slotResp.data && Array.isArray(slotResp.data))
                          slotsData = slotResp.data;
                        else if (Array.isArray(slotResp)) slotsData = slotResp;

                        const branchSlotEvents = buildSlotEvents(
                          slotsData,
                          branchBizId
                        );
                        slotEvents = [...slotEvents, ...branchSlotEvents];
                      } catch (error) {
                        console.error(
                          "Error fetching slots for branch:",
                          branch?.id,
                          error
                        );
                      }
                    }
                  } else {
                    const slotResp = await getSlots({
                      business_id: targetBusinessId,
                      start_date: startDateParam,
                      end_date: endDateParam,
                    });
                    let slotsData: any[] = [];
                    if (!slotResp) slotsData = [];
                    else if (slotResp.history && Array.isArray(slotResp.history))
                      slotsData = slotResp.history;
                    else if (slotResp.slots) slotsData = slotResp.slots;
                    else if (slotResp.data && Array.isArray(slotResp.data))
                      slotsData = slotResp.data;
                    else if (Array.isArray(slotResp)) slotsData = slotResp;

                    slotEvents = buildSlotEvents(slotsData, targetBusinessId);
                  }

                  setEvents([...mapped, ...slotEvents]);
                  setCalendarKey((prev) => prev + 1);
                } catch (e) {
                  console.warn("Failed to fetch slots after drag update", e);
                  setEvents(mapped);
                  setCalendarKey((prev) => prev + 1);
                }
              } catch (error) {
                console.error("Error updating appointment after drag:", error);
                
                // Restore the event if it was removed (branch change scenario)
                if (eventWasRemoved && originalEvent) {
                  setEvents((prev) => {
                    const current = Array.isArray(prev) ? prev : [];
                    // Check if event already exists (shouldn't, but be safe)
                    const exists = current.some((e) => String(e.id) === String(eventId));
                    if (!exists) {
                      return [...current, originalEvent];
                    }
                    return current;
                  });
                }
                
                toast.error("Failed to update appointment. Changes reverted.");
                // Throw error so GridCalendar can reset the visual position
                throw error;
              }
            }}
          />
        )}
      </Box>
      <GenericFormDialog
        open={addDialogOpen}
        onClose={handleAppointmentDialogClose}
        title={dialogTitle}
        submitLabel={dialogSubmitLabel}
        disabled={isReadOnlyAppointment}
        showSubmit={dialogShowSubmit}
        initialValues={dialogInitialValues}
        additionalActions={appointmentDialogActions}
        fields={appointmentFields}
        tabs={dialogTabs}
        initialTab={appointmentDialogTab}
        onTabChange={setAppointmentDialogTab}
        onSubmit={async (values) => {
          if (!editingEventId && appointmentDialogTab === 1) {
            await handleBlockSlotSubmit(values);
            return;
          }
          await handleAppointmentSubmit(values);
        }}
      />
      <GenericFormDialog
        open={clientEditOpen}
        onClose={() => {
          setClientEditOpen(false);
          setEditingClient(null);
        }}
        title="Edit Client"
        submitLabel={clientEditLoading ? "Saving..." : "Save"}
        fields={clientEditFields}
        initialValues={clientEditInitialValues}
        onSubmit={handleClientEditSubmit}
        disabled={clientEditLoading}
      />
      <GenericFormDialog
        open={invoiceEditOpen}
        onClose={handleInvoiceDialogClose}
        title="Edit Invoice"
        submitLabel="Update"
        initialValues={invoiceDialogInitialValues}
        fields={invoiceFormFields}
        onSubmit={handleInvoiceSubmit}
      />
    </div>
  );
}

export default Appointment;
